import React, { useState, useEffect } from 'react';
import { Settings, Save, RotateCcw, Info, Sliders, Palette, Zap, List, Plus, X, Edit2, HelpCircle } from 'lucide-react';
import { API_URL } from '../../config';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

export default function AstriaTuningSettings({ adminToken }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [originalSettings, setOriginalSettings] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState('v1');
  const [dropdowns, setDropdowns] = useState(null);
  const [isEditingDropdowns, setIsEditingDropdowns] = useState(false);
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const [helpContent, setHelpContent] = useState({ title: '', description: '', example: '' });
  const [modelOptions, setModelOptions] = useState(null); // NEU: Dynamische Model Options!

  useEffect(() => {
    loadSettings(selectedVersion);
    loadDropdowns();
    loadModelOptions(selectedVersion); // NEU: Lade Model Options
  }, []);

  const loadModelOptions = async (version = selectedVersion) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/modes/public/model-options/${version}`);
      if (res.ok) {
        const data = await res.json();
        setModelOptions(data);
      }
    } catch (error) {
      console.error('Error loading model options:', error);
    }
  };

  const loadSettings = async (version = selectedVersion) => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/admin/astria-tuning/settings/${version}`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });

      if (!res.ok) throw new Error('Failed to load settings');

      const data = await res.json();
      setSettings(data);
      setOriginalSettings(JSON.parse(JSON.stringify(data))); // Deep copy
      setIsEditing(false); // Start im Read-Only Modus
      setSelectedVersion(version); // Update selected version
    } catch (error) {
      console.error('Error loading settings:', error);
      showMessage('Fehler beim Laden der Einstellungen', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Load settings when version changes
  useEffect(() => {
    loadSettings(selectedVersion);
    loadModelOptions(selectedVersion); // NEU: Reload model options bei Version-Wechsel
  }, [selectedVersion]);

  const handleVersionChange = (newVersion) => {
    if (isEditing) {
      if (!window.confirm('Änderungen verwerfen und Version wechseln?')) return;
      setIsEditing(false);
    }
    setSelectedVersion(newVersion);
  };

  const loadDropdowns = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/astria-tuning/dropdowns`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDropdowns(data);
      }
    } catch (error) {
      console.error('Error loading dropdowns:', error);
    }
  };

  const saveDropdowns = async () => {
    try {
      setSaving(true);
      const res = await fetch(`${API_URL}/api/admin/astria-tuning/dropdowns`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify(dropdowns)
      });

      if (!res.ok) throw new Error('Failed to save dropdowns');

      setIsEditingDropdowns(false);
      showMessage('✅ Dropdown-Optionen gespeichert!', 'success');
      await loadDropdowns();
    } catch (error) {
      console.error('Error saving dropdowns:', error);
      showMessage('❌ Fehler beim Speichern der Dropdowns', 'error');
    } finally {
      setSaving(false);
    }
  };

  const addDropdownOption = (category, key) => {
    // Bildformate haben width/height, andere haben keywords
    const newOption = category === 'formats' 
      ? {label_de: '', desc_de: '', width: 1024, height: 1024}
      : {label_de: '', desc_de: '', keywords: ''};
    
    setDropdowns(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: newOption
      }
    }));
  };

  const updateDropdownField = (category, key, field, value) => {
    setDropdowns(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: {
          ...prev[category][key],
          [field]: value
        }
      }
    }));
  };

  const deleteDropdownOption = (category, key) => {
    setDropdowns(prev => {
      const newCat = { ...prev[category] };
      delete newCat[key];
      return { ...prev, [category]: newCat };
    });
  };


  const startEditing = () => {
    setOriginalSettings(JSON.parse(JSON.stringify(settings))); // Backup erstellen
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setSettings(JSON.parse(JSON.stringify(originalSettings))); // Wiederherstellen
    setIsEditing(false);
    showMessage('Änderungen verworfen', 'info');
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      const res = await fetch(`${API_URL}/api/admin/astria-tuning/settings/${selectedVersion}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify(settings)
      });

      if (!res.ok) throw new Error('Failed to save settings');

      setIsEditing(false); // Zurück zu Read-Only
      const versionLabel = selectedVersion === 'v1' ? 'V1 (ID-Card)' : 'V2 (Snapshot Studio)';
      showMessage(`✅ ${versionLabel} Einstellungen erfolgreich gespeichert!`, 'success');
      await loadSettings(selectedVersion); // Reload to get updated timestamp
    } catch (error) {
      console.error('Error saving settings:', error);
      showMessage('❌ Fehler beim Speichern', 'error');
    } finally {
      setSaving(false);
    }
  };

  const resetSettings = async () => {
    if (!window.confirm('Wirklich alle Einstellungen auf Standard zurücksetzen?')) return;

    try {
      setSaving(true);
      const res = await fetch(`${API_URL}/api/admin/astria-tuning/settings/reset`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });

      if (!res.ok) throw new Error('Failed to reset settings');

      showMessage('✅ Einstellungen zurückgesetzt!', 'success');
      await loadSettings();
    } catch (error) {
      console.error('Error resetting settings:', error);
      showMessage('❌ Fehler beim Zurücksetzen', 'error');
    } finally {
      setSaving(false);
    }
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 4000);
  };

  const updateField = (field, value) => {
    if (!isEditing) return; // Nur im Edit-Modus erlauben
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  // Helper: Zeige aktuellen Wert im Read-Only Modus
  const CurrentValue = ({ children }) => {
    if (isEditing) return null;
    return (
      <div className="mt-2 text-sm text-[#10B981]">
        Aktuell: <span className="font-semibold">{children}</span>
      </div>
    );
  };
  
  // Helper: Laienhafte Erklärung für Praktikanten
  const ExplainText = ({ children }) => {
    return (
      <div className="mt-1 text-xs text-white/50 leading-relaxed">
        💡 {children}
      </div>
    );
  };
  
  // Helper: Show Help Modal
  const showHelp = (title, description, example = '') => {
    setHelpContent({ title, description, example });
    setHelpModalOpen(true);
  };

  // Helper: Help Icon Button
  const HelpIcon = ({ title, description, example }) => (
    <button
      onClick={(e) => {
        e.stopPropagation();
        showHelp(title, description, example);
      }}
      className="ml-2 inline-flex items-center justify-center w-5 h-5 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
      type="button"
      data-testid={`help-icon-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <HelpCircle className="w-3.5 h-3.5 text-[#10B981]" />
    </button>
  );

  // Laienhafte Erklärungen für alle Parameter
  const explanations = {
    // Training
    base_tune_id: "Welches KI-Modell soll als Grundlage verwendet werden? 'Flux.1 dev' ist am stabilsten und wird empfohlen.",
    model_type: "Welche Trainings-Methode? LoRA ist schnell und effizient (empfohlen). Dreambooth ist langsamer aber detaillierter.",
    preset: "Welche voreingestellte Konfiguration? 'Flux LoRA Portrait' ist optimal für Gesichter.",
    branch: "Welche Modell-Version? 'Flux1' ist die neueste Generation.",
    training_token: "Ein spezielles Wort (z.B. 'ohwx'), das die KI mit den Trainingsbildern verknüpft. Nicht ändern!",
    training_name: "Kategorie der Person: 'woman' für Frauen, 'man' für Männer, 'person' für allgemein.",
    training_steps: "Wie lange soll trainiert werden? 432 Steps sind optimal. Mehr = besser aber langsamer, weniger = schneller aber schlechter.",
    face_crop: "Sollen Gesichter automatisch aus den Trainingsbildern ausgeschnitten und als Extra-Bilder hinzugefügt werden? Verbessert Gesichts-Qualität.",
    training_face_correct: "Sollen Trainingsbilder vor dem Training automatisch verbessert werden (GFPGAN)? Hilft bei schlechter Bildqualität.",
    auto_extend: "Soll das Training automatisch verlängert werden, wenn es abläuft? Kostet extra.",
    
    // Generation Core
    cfg_scale: "Wie genau soll das Bild dem Prompt folgen? Niedrig (1-5) = kreativ und überraschend. Hoch (10-20) = exakt nach Text aber weniger Variation.",
    steps: "Wie viele Rechenschritte? Mehr Steps = bessere Qualität aber langsamer. 30 ist ein guter Mittelwert.",
    num_images: "Wie viele Bilder pro Generation? 1-8 möglich. Wird überschrieben wenn User selbst auswählt.",
    seed: "Eine feste Zahl für identische Ergebnisse. Leer = jedes Mal anders. Mit Zahl = immer gleich.",
    
    // Generation Advanced
    base_prompt_suffix: "Dieser Text wird automatisch an JEDEN User-Prompt angehängt. Nutzen für Qualitäts-Verbesserungen.",
    negative_prompt: "Was soll NICHT im Bild sein? z.B. 'verschwommen, schlechte Qualität, Fehler'",
    scheduler: "Welcher Algorithmus? 'dpmpp_2m' ist gut für realistische Bilder. Standard lässt die KI entscheiden.",
    similarity: "Wie ähnlich soll das generierte Bild der Trainingsperson sein? 0.0 = egal, 1.0 = exakt gleich.",
    lora_weight: "Wie stark soll der LoRA-Effekt sein? 1.0 = normal, niedriger = schwächer, höher = stärker.",
    tune_weight: "Wie stark soll das Training wirken? 1.0 = normal, niedriger = mehr Original-Modell, höher = mehr Training.",
    use_lpw: "Gewichtete Prompts aktivieren? Ermöglicht (text:1.5) Syntax für Schwerpunkte im Prompt.",
    color_grading: "Farb-Look des Bildes? 'Film Velvia' = kräftige Farben, 'Film Portra' = weiche Hauttöne, 'Ektar' = warm.",
    film_grain: "Soll analoges Film-Körnung hinzugefügt werden? Macht Bilder vintage/retro.",
    aspect_ratio: "Seitenverhältnis des Bildes. 16:9 = Querformat, 9:16 = Hochformat, 1:1 = Quadrat.",
    denoising_strength: "Für Remix/img2img: Wie viel vom Original behalten? 0.0 = Original komplett, 1.0 = komplett neu nach Prompt.",
    controlnet: "Erweiterte Bild-Kontrolle. Nur für Spezialfälle. Normal = leer lassen.",
    backend_version: "Welche Backend-Version? Leer = neueste. Nur ändern wenn Probleme auftreten.",
    
    // Quality Features
    super_resolution: "Soll das Bild hochskaliert werden (4x größer)? Verbessert Details, dauert länger.",
    face_correct: "Sollen Gesichter nachträglich korrigiert werden? Verbessert Gesichtsdetails.",
    inpaint_faces: "Sollen Gesichter nachträglich nachgemalt werden? Macht Gesichter glatter.",
    face_swap: "Soll das Gesicht aus den Trainingsbildern verwendet werden? Verbessert Ähnlichkeit.",
    hires_fix: "Hochauflösungs-Fix? Fügt mehr Details hinzu, besonders bei großen Bildern.",
    style: "Visueller Stil des Bildes. 'Photographic' = fotorealistisch, 'Cinematic' = filmisch, etc."
  };

  if (loading || !settings) {
    return (
      <div className="min-h-screen bg-black grain-texture flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 border-4 border-[#10B981] border-t-transparent rounded-full animate-spin"></div>
          <p className="text-white/70">Lade Astria Tuning Einstellungen...</p>
        </div>
      </div>
    );
  }

  if (!modelOptions) {
    return (
      <div className="min-h-screen bg-black grain-texture flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 border-4 border-[#10B981] border-t-transparent rounded-full animate-spin"></div>
          <p className="text-white/70">Lade Modell-Optionen...</p>
        </div>
      </div>
    );
  }

  // Dynamische Dropdown Options aus DB
  const baseTuneOptions = modelOptions.base_tune_options?.sort((a, b) => a.order - b.order) || [];
  const modelTypeOptions = modelOptions.model_type_options?.sort((a, b) => a.order - b.order) || [];
  const presetOptions = modelOptions.preset_options?.sort((a, b) => a.order - b.order) || [];
  const branchOptions = modelOptions.branch_options?.sort((a, b) => a.order - b.order) || [];
  const colorGradingOptions = modelOptions.color_grading_options?.sort((a, b) => a.order - b.order) || [];
  const aspectRatioOptions = modelOptions.aspect_ratio_options?.sort((a, b) => a.order - b.order) || [];
  const controlnetOptions = modelOptions.controlnet_options?.sort((a, b) => a.order - b.order) || [];
  const styleOptions = modelOptions.style_options?.sort((a, b) => a.order - b.order) || [];
  const schedulerOptions = modelOptions.scheduler_options?.sort((a, b) => a.order - b.order) || [];

  return (
    <div className="min-h-screen bg-black grain-texture p-2 sm:p-4 lg:p-6 overscroll-none">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              <Settings className="w-6 h-6 sm:w-8 sm:h-8 text-[#10B981]" />
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-white">Astria Tuning</h1>
                <p className="text-white/60 text-xs sm:text-sm mt-1">
                  Zentrale Verwaltung aller Astria API Parameter für Training und Bildgenerierung
                </p>
              </div>
            </div>
            
            {/* Version Dropdown */}
            <div className="flex items-center gap-3">
              <label className="text-white/80 font-medium text-sm sm:text-base">System:</label>
              <select
                value={selectedVersion}
                onChange={(e) => handleVersionChange(e.target.value)}
                disabled={isEditing}
                className="px-3 sm:px-4 py-2.5 sm:py-2 bg-black/60 border border-white/20 rounded-xl text-white font-semibold focus:border-[#10B981] focus:outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base min-h-[44px] touch-target"
                data-testid="version-select"
              >
                <option value="v1">V1 (ID-Card / Julia)</option>
                <option value="v2">V2 (Snapshot Studio)</option>
              </select>
            </div>
          </div>
        </div>

        {/* Message Toast */}
        {message && (
          <div className={`mb-6 p-4 rounded-xl border ${
            message.type === 'success' 
              ? 'bg-[#10B981]/10 border-[#10B981]/30 text-[#10B981]'
              : 'bg-red-500/10 border-red-500/30 text-red-400'
          }`}>
            {message.text}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6 sm:mb-8">
          {!isEditing ? (
            // Read-Only Modus: Bearbeiten Button
            <button
              onClick={startEditing}
              className="flex items-center justify-center gap-2 px-6 py-3.5 bg-[#10B981] hover:bg-[#0EA574] text-black font-semibold rounded-xl transition-all min-h-[44px] touch-target"
              data-testid="astria-tuning-edit-btn"
            >
              <Settings className="w-5 h-5" />
              Bearbeiten
            </button>
          ) : (
            // Edit-Modus: Speichern & Abbrechen Buttons
            <>
              <button
                onClick={saveSettings}
                disabled={saving}
                className="flex items-center justify-center gap-2 px-6 py-3.5 bg-[#10B981] hover:bg-[#0EA574] text-black font-semibold rounded-xl transition-all disabled:opacity-50 min-h-[44px] touch-target"
                data-testid="astria-tuning-save-btn"
              >
                <Save className="w-5 h-5" />
                {saving ? 'Speichere...' : 'Speichern'}
              </button>

              <button
                onClick={cancelEditing}
                disabled={saving}
                className="flex items-center justify-center gap-2 px-6 py-3.5 bg-white/5 hover:bg-white/10 text-white border border-white/20 font-semibold rounded-xl transition-all disabled:opacity-50 min-h-[44px] touch-target"
              >
                <RotateCcw className="w-5 h-5" />
                Abbrechen
              </button>
            </>
          )}

          {!isEditing && (
            <button
              onClick={resetSettings}
              disabled={saving}
              className="flex items-center justify-center gap-2 px-6 py-3.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 font-semibold rounded-xl transition-all disabled:opacity-50 min-h-[44px] touch-target"
              data-testid="astria-tuning-reset-btn"
            >
              <RotateCcw className="w-5 h-5" />
              <span className="hidden sm:inline">Auf Standard zurücksetzen</span>
              <span className="sm:hidden">Zurücksetzen</span>
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          {/* Training Parameters */}
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-6">
              <Sliders className="w-6 h-6 text-[#10B981]" />
              <h2 className="text-xl font-bold text-white">Training Parameter</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  Base Model (Tune ID)
                  <HelpIcon 
                    title="Base Model (Tune ID)" 
                    description="Stell dir vor, du gehst in einen Künstler-Laden und kannst verschiedene Künstler engagieren. Jeder Künstler (=Modell) hat seinen eigenen Stil:\n\n• 'Flux.1 dev' ist wie ein professioneller Fotograf - macht super realistische Fotos von Menschen. DAS ist unser Standard-Künstler!\n• 'Disney Pixar' ist wie ein Cartoon-Zeichner - macht Comic-Style Bilder\n• 'Flux.1 Mini' ist wie ein schneller Skizzen-Künstler - macht schnelle Entwürfe\n\nWICHTIG: Für 99% aller Fälle nimm einfach 'Flux.1 dev' - der macht die besten Porträt-Fotos!"
                    example="Du willst ein realistisches Foto von Julia am Strand?\n→ Nimm 'Flux.1 dev (1504944)' ← IMMER DIESE WAHL!\n\nDu willst einen Cartoon-Avatar?\n→ Dann Disney Pixar (987654)\n\nMERKE DIR: Im Zweifel IMMER Flux.1 dev nehmen!"
                  />
                </label>
                <select
                  value={settings.base_tune_id}
                  onChange={(e) => updateField('base_tune_id', parseInt(e.target.value))}
                  disabled={!isEditing}
                  className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="base-tune-select"
                >
                  {baseTuneOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                {!isEditing && (
                  <div className="mt-2 text-sm text-[#10B981]">
                    Aktuell: <span className="font-semibold">{baseTuneOptions.find(o => o.value === settings.base_tune_id)?.label || settings.base_tune_id}</span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  Model Type
                  <HelpIcon 
                    title="Model Type (Trainings-Methode)" 
                    description="Denk an eine Fahrschule:\n\n• LoRA = Intensiv-Kurs (5 Tage) - du lernst schnell das Wichtigste zum Fahren. Reicht für die meisten! ← NIMM DAS!\n• Dreambooth = Langzeit-Kurs (3 Monate) - du lernst ALLES über Autos, Motoren, Reparatur. Sehr detailliert, aber dauert ewig.\n• Flux = Elektro-Auto-Kurs - neueste Technologie, beste Qualität\n• FaceID = Express-Kurs (1 Tag) - super schnell, aber du lernst nur die Basics\n\nFür unsere Porträt-Fotos ist LoRA perfekt! Schnell UND gute Qualität."
                    example="Julia soll in 10 verschiedenen Szenen fotografiert werden:\n→ LoRA wählen = Fertig in 15 Minuten, super Qualität ← STANDARD\n→ Dreambooth wählen = Fertig in 2 Stunden, minimal bessere Qualität (lohnt sich nicht!)\n\nMERKE: Fast IMMER LoRA nehmen!"
                  />
                </label>
                <ExplainText>
                  Welche Trainings-Methode? LoRA ist schnell und effizient (empfohlen). Dreambooth ist langsamer aber detaillierter.
                </ExplainText>
                <select
                  value={settings.model_type || 'lora'}
                  onChange={(e) => updateField('model_type', e.target.value)}
                  disabled={!isEditing}
                  className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="model-type-select"
                >
                  {modelTypeOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                {!isEditing && (
                  <div className="mt-2 text-sm text-[#10B981]">
                    Aktuell: <span className="font-semibold">{modelTypeOptions.find(o => o.value === (settings.model_type || 'lora'))?.label}</span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  Training Preset
                  <HelpIcon 
                    title="Training Preset" 
                    description="Stell dir vor, du backst einen Kuchen. Du könntest alle Zutaten einzeln zusammensuchen... ODER du nimmst eine fertige Backmischung wo schon alles perfekt abgemessen ist!\n\n'Flux LoRA Portrait' ist so eine perfekte Backmischung - speziell für Gesichter. Alle komplizierten Einstellungen sind schon optimal eingestellt.\n\nDu musst NIE ein anderes Preset nehmen. Flux LoRA Portrait funktioniert IMMER am besten für Menschen-Fotos."
                    example="Szenario: Du willst ein Foto von Sebastian am Strand generieren\n\nFalsch: Verschiedene Presets ausprobieren (verschwendete Zeit!)\nRichtig: 'Flux LoRA Portrait' wählen → Fertig! ← IMMER DIESE WAHL\n\nDenk dran: Flux LoRA Portrait ist wie die 'Easy Mode' Taste - macht alles automatisch richtig!"
                  />
                </label>
                <select
                  value={settings.preset || 'flux-lora-portrait'}
                  onChange={(e) => updateField('preset', e.target.value)}
                  disabled={!isEditing}
                  className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="preset-select"
                >
                  {presetOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                {!isEditing && (
                  <div className="mt-2 text-sm text-[#10B981]">
                    Aktuell: <span className="font-semibold">{presetOptions.find(o => o.value === (settings.preset || 'flux-lora-portrait'))?.label}</span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  Branch
                  <HelpIcon 
                    title="Branch (Modell-Version)" 
                    description="Denk an iPhone-Versionen:\n\n• Flux1 = iPhone 15 (neustes Modell, beste Kamera, schnellster Prozessor) ← DAS NIMMST DU!\n• SDXL1 = iPhone 12 (noch okay, aber schon älter)\n• SD1.5 = iPhone 8 (alt, langsam, nur für alte Apps)\n• Fast = Spielzeug-Handy (nur zum Testen, keine echten Fotos)\n\nDu willst doch das beste Ergebnis, oder? Also IMMER Flux1 nehmen! Die anderen Optionen sind nur da, falls jemand mit einem uralten System arbeitet."
                    example="Neues Projekt mit Julia starten:\n→ Flux1 wählen ← STANDARD, IMMER!\n\nAltes Projekt von 2022 weiterführen:\n→ Dann vielleicht SD1.5 (aber besser neu mit Flux1 starten!)\n\nEINFACH MERKEN: Flux1 = Beste Wahl. Punkt."
                  />
                </label>
                <select
                  value={settings.branch || 'flux1'}
                  onChange={(e) => updateField('branch', e.target.value)}
                  disabled={!isEditing}
                  className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="branch-select"
                >
                  {branchOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                {!isEditing && (
                  <div className="mt-2 text-sm text-[#10B981]">
                    Aktuell: <span className="font-semibold">{branchOptions.find(o => o.value === (settings.branch || 'flux1'))?.label}</span>
                  </div>
                )}
              </div>

              {/* Training Toggles */}
              <div className="space-y-3 mt-4">
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">
                    Face Crop
                    <HelpIcon 
                      title="Face Crop" 
                      description="Okay, pass auf - das ist wie Hausaufgaben machen:\n\nDu gibst der KI 10 Fotos von Julia. Normalerweise schaut sich die KI alle 10 Fotos komplett an (Körper, Hintergrund, Gesicht, alles).\n\nMIT Face Crop:\nDie KI schneidet AUTOMATISCH Julias Gesicht aus jedem Foto aus und macht daraus 10 EXTRA Nahaufnahmen. Jetzt hat die KI 10 normale Fotos PLUS 10 Gesichts-Nahaufnahmen = 20 Lernbilder!\n\nWARUM GUT? Die KI lernt Gesichter VIEL besser wenn sie extra Nahaufnahmen hat. Wie wenn du für eine Prüfung: Einmal das ganze Buch liest UND nochmal extra die wichtigen Kapitel!"
                      example="Szenario: Training mit 10 Julia-Fotos\n\nOHNE Face Crop:\n→ KI bekommt 10 Fotos zum Lernen\n→ Gesichter-Qualität: 7/10\n\nMIT Face Crop (EMPFOHLEN!):\n→ KI bekommt 10 normale + 10 Gesichts-Nahaufnahmen = 20 Bilder\n→ Gesichter-Qualität: 9/10\n\nFAZIT: IMMER aktivieren! Kostet nichts extra, macht Gesichter deutlich besser!"
                    />
                  </label>
                  <div className="text-sm text-white/50 mb-2">💡 Gesichter in Training-Bildern croppen</div>
                  <select
                    value={settings.face_crop ? 'true' : 'false'}
                    onChange={(e) => updateField('face_crop', e.target.value === 'true')}
                    disabled={!isEditing}
                    className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="face-crop-select"
                  >
                    <option value="false">Aus</option>
                    <option value="true">An</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">
                    Training Face Correct
                    <HelpIcon 
                      title="Training Face Correct (GFPGAN)" 
                      description="Stell dir vor, du hast unscharfe, verpixelte oder dunkle Handy-Fotos von Julia.\n\nOHNE Face Correct:\nDie KI lernt mit den schlechten Fotos → später macht sie auch schlechte Bilder\n\nMIT Face Correct (GFPGAN):\n1. Die KI nimmt zuerst jedes Foto\n2. Repariert automatisch: Unschärfe, Pixel, dunkle Bereiche, Hautunreinheiten\n3. DANN lernt die KI mit den reparierten, schönen Fotos\n4. Ergebnis: Später generiert sie auch schöne Bilder!\n\nWie Photoshop BEVOR die KI lernt!\n\nWICHTIG: Wenn deine Trainings-Fotos schon perfekt sind (Studio-Fotos), brauchst du es nicht. Aber bei normalen Handy-Fotos? AUF JEDEN FALL aktivieren!"
                      example="Situation 1: Handy-Fotos von Julia (etwas unscharf)\n→ Training Face Correct AN ← WICHTIG!\n→ KI repariert Fotos automatisch vor dem Training\n→ Ergebnis wird 50% besser!\n\nSituation 2: Professionelle Studio-Fotos (schon perfekt)\n→ Training Face Correct AUS (nicht nötig)\n→ Fotos sind schon gut genug\n\nZWEIFEL? Lieber AN lassen - schadet nie!"
                    />
                  </label>
                  <div className="text-sm text-white/50 mb-2">💡 GFPGAN Enhancement für Training-Bilder</div>
                  <select
                    value={settings.training_face_correct ? 'true' : 'false'}
                    onChange={(e) => updateField('training_face_correct', e.target.value === 'true')}
                    disabled={!isEditing}
                    className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="training-face-correct-select"
                  >
                    <option value="false">Aus</option>
                    <option value="true">An</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">
                    Auto Extend
                    <HelpIcon 
                      title="Auto Extend" 
                      description="Stell dir vor, du hast ein Fitness-Studio-Abo:\n\nOHNE Auto Extend:\n• Du zahlst für 1 Monat Training\n• Nach 1 Monat ist das Abo abgelaufen\n• Wenn du weiter trainieren willst, musst du manuell verlängern\n• Vergisst du es? Pech, Training weg!\n\nMIT Auto Extend:\n• Du zahlst für 1 Monat\n• Nach 1 Monat verlängert sich das Abo AUTOMATISCH\n• Du kannst jederzeit weiter trainieren\n• ABER: Es wird automatisch weiter abgebucht! (kostet Geld!)\n\nFür Astria: Trainings 'laufen ab' nach einer Zeit. Mit Auto Extend bleiben sie aktiv, kosten aber weiter Geld.\n\nMEINE EMPFEHLUNG: AUS lassen! Manuell verlängern wenn wirklich nötig."
                      example="Fall 1: Test-Projekt mit Julia (nur 1 Woche nutzen)\n→ Auto Extend AUS ← EMPFOHLEN\n→ Nach 1 Woche läuft Training ab\n→ Kein Geld verschwendet!\n\nFall 2: Wichtiger Dauer-Kunde (muss immer verfügbar sein)\n→ Auto Extend AN\n→ Training bleibt für immer aktiv\n→ Kostet weiter Geld, aber kein Ausfall!\n\nREGEL: Im Zweifel AUS! Lieber manuell verlängern."
                    />
                  </label>
                  <div className="text-sm text-white/50 mb-2">💡 Automatische Verlängerung bei Ablauf</div>
                  <select
                    value={settings.auto_extend ? 'true' : 'false'}
                    onChange={(e) => updateField('auto_extend', e.target.value === 'true')}
                    disabled={!isEditing}
                    className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="auto-extend-select"
                  >
                    <option value="false">Aus</option>
                    <option value="true">An</option>
                  </select>
                </div>
              
              {/* NEU: FaceID (Training) */}
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">
                    FaceID Training
                    <HelpIcon 
                      title="FaceID Training (Gesichts-Fingerabdruck)" 
                      description="FaceID ist wie ein Fingerabdruck für Gesichter!\n\nNORMAL (ohne FaceID):\nDie KI schaut sich Julias Fotos an und denkt: 'Okay, so sieht Julia ungefähr aus - runde Augen, dunkle Haare, etc.'\n→ Später generiert sie Bilder die ÄHNLICH aussehen (85% Ähnlichkeit)\n\nMIT FaceID:\n1. KI macht einen EXAKTEN Fingerabdruck von Julias Gesicht\n2. Speichert PRÄZISE: Augenabstand, Nasenbreite, Kinnform\n3. Beim Generieren: KI nutzt diesen Fingerabdruck\n4. Ergebnis: EXAKT Julia! (95-98% Ähnlichkeit!)\n\nVORTEILE:\n• Deutlich bessere Gesichts-Ähnlichkeit\n• Person ist sofort erkennbar\n• Konsistente Ergebnisse"
                      example="OHNE FaceID:\n→ Ähnlichkeit: 85%\n→ Manchmal Nase anders, Augen nicht ganz gleich\n\nMIT FaceID:\n→ Ähnlichkeit: 98%\n→ EXAKT Julia! Jedes Mal!\n\nEMPFEHLUNG: Bei Porträts IMMER aktivieren!"
                    />
                  </label>
                  <div className="text-sm text-white/50 mb-2">💡 Aktiviert FaceID für maximale Gesichts-Ähnlichkeit</div>
                  <select
                    value={settings.training_faceid ? 'true' : 'false'}
                    onChange={(e) => updateField('training_faceid', e.target.value === 'true')}
                    disabled={!isEditing}
                    className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="training-faceid-select"
                  >
                    <option value="false">Aus</option>
                    <option value="true">An</option>
                  </select>
                </div>
                
                {/* NEU: Remove Background (Training) */}
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">
                    Remove Background (Training)
                    <HelpIcon 
                      title="Remove Background Training" 
                      description="Hintergrund automatisch aus Trainingsbildern entfernen.\n\nNORMAL: KI lernt Julia + Hintergrund zusammen\nMIT Remove BG: KI lernt NUR Julia (ohne Ablenkung)\n\nVorteil: Julia funktioniert in JEDEM Hintergrund perfekt!"
                      example="Mit Remove BG:\n→ KI lernt NUR Julia\n→ Später funktioniert sie überall!\n\nEMPFEHLUNG: Aktivieren für maximale Flexibilität!"
                    />
                  </label>
                  <div className="text-sm text-white/50 mb-2">💡 Entfernt Hintergrund aus Trainingsbildern</div>
                  <select
                    value={settings.training_remove_bg ? 'true' : 'false'}
                    onChange={(e) => updateField('training_remove_bg', e.target.value === 'true')}
                    disabled={!isEditing}
                    className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="training-remove-bg-select"
                  >
                    <option value="false">Aus</option>
                    <option value="true">An</option>
                  </select>
                </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  Training Token
                  <span className="ml-2 text-white/50">(z.B. &quot;ohwx&quot;)</span>
                  <HelpIcon 
                    title="Training Token" 
                    description="Okay, das ist SUPER wichtig zu verstehen:\n\nStell dir vor, die KI ist wie ein Hund. Du trainierst ihn mit dem Befehl 'Sitz!' - ab jetzt weiß der Hund: 'Sitz = Hinsetzen'.\n\nGENAUSO funktioniert der Token:\n• Du sagst der KI: 'ohwx = Julia aus den Trainingsbildern'\n• Die KI lernt: 'Aha! Immer wenn jemand ohwx sagt, meint er DIESE spezielle Person!'\n\nSpäter beim Generieren:\n• Prompt: 'ohwx woman on beach' → KI denkt: 'Ah, ohwx = Julia! Ich mache Julia am Strand!'\n• Prompt: 'woman on beach' → KI denkt: 'Okay, irgendeine Frau am Strand' (NICHT Julia!)\n\nDer Token ist wie Julias GEHEIMER CODE!\n\nWICHTIG: NIE ÄNDERN wenn Training schon läuft! Sonst verliert die KI die Verbindung!"
                    example="RICHTIGER Ablauf:\n1. Token wählen: 'ohwx'\n2. Training starten mit Julia-Fotos\n3. KI lernt: ohwx = Julia\n4. Später Prompt: 'ohwx woman in red dress'\n5. Ergebnis: Foto von JULIA in rotem Kleid! ✓\n\nFALSCHER Ablauf:\n1. Token: 'ohwx'\n2. Training läuft...\n3. Token ändern zu 'abc' (FEHLER!)\n4. Prompt: 'abc woman in red dress'\n5. KI: 'Wer ist abc??' (kennt nur ohwx!)\n6. Ergebnis: Irgendeine Frau, NICHT Julia! ✗\n\nREGEL: Token NIE ändern! 'ohwx' ist perfekt, lass es so!"
                  />
                </label>
                <ExplainText>{explanations.training_token}</ExplainText>
                <input
                  type="text"
                  value={settings.training_token}
                  onChange={(e) => updateField('training_token', e.target.value)}
                  disabled={!isEditing}
                  className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="training-token-input"
                />
                {!isEditing && (
                  <div className="mt-2 text-sm text-[#10B981]">
                    Aktuell: <span className="font-semibold">{settings.training_token}</span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  Training Name
                  <span className="ml-2 text-white/50">(automatisch aus User-Gender)</span>
                  <HelpIcon 
                    title="Training Name (Automatisch gesetzt)" 
                    description="Das ist die Kategorie der Person. Sag der KI, OB es eine Frau, ein Mann oder allgemein eine Person ist. Das hilft der KI, die richtigen Details zu lernen (z.B. Bart bei Männern, Make-up bei Frauen). Falsche Kategorie = schlechtere Ergebnisse!\n\n⚠️ WICHTIG: Dieses Feld wird AUTOMATISCH gesetzt!\n\nWenn ein User durch den Wizard geht und sein Geschlecht auswählt:\n• User wählt 'Frau' → System setzt automatisch 'woman'\n• User wählt 'Mann' → System setzt automatisch 'man'\n• User wählt 'Kind' → System setzt automatisch 'person'\n\nDu musst hier NICHTS manuell ändern! Das System macht das automatisch für jeden User basierend auf seinen Wizard-Angaben."
                    example="Trainiere Julia → Wizard-Auswahl: 'Frau'\n→ System setzt automatisch: 'woman' ✓\n→ KI lernt Frauen-Details (Make-up, Frisuren)\n\nTrainiere Sebastian → Wizard-Auswahl: 'Mann'\n→ System setzt automatisch: 'man' ✓\n→ KI lernt Männer-Details (Bart, Kinnlinie)\n\nTrainiere Kind (10 Jahre) → Wizard-Auswahl: 'Kind'\n→ System setzt automatisch: 'person' ✓\n→ Geschlechtsneutrale Details\n\n⚠️ DIESES FELD IST NUR ZUR INFO!\nDer Wert hier ist der Standard-Fallback.\nJeder User bekommt automatisch den richtigen Wert!"
                  />
                </label>
                
                <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-white/80">
                      <p className="font-semibold mb-1">ℹ️ Automatische Erkennung aktiv</p>
                      <p className="text-white/60">
                        Dieser Wert wird automatisch aus den User-Daten (Wizard) gesetzt:
                      </p>
                      <ul className="mt-2 space-y-1 text-xs text-white/50">
                        <li>• Frau → &quot;woman&quot;</li>
                        <li>• Mann → &quot;man&quot;</li>
                        <li>• Kind → &quot;person&quot;</li>
                      </ul>
                      <p className="mt-2 text-xs text-blue-300">
                        Der Wert unten ist nur der Standard-Fallback falls keine Gender-Info vorhanden ist.
                      </p>
                    </div>
                  </div>
                </div>
                
                <input
                  type="text"
                  value={settings.training_name}
                  disabled
                  className="mt-3 w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white/50 cursor-not-allowed"
                  data-testid="training-name-input"
                />
                <div className="mt-2 text-xs text-white/40 italic">
                  🔒 Schreibgeschützt - Wird automatisch pro User gesetzt
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  Training Steps: {settings.training_steps}
                  <HelpIcon 
                    title="Training Steps" 
                    description="Stell dir vor, du lernst Vokabeln für einen Test:\n\n• 100 Steps = Du liest jede Vokabel 100 mal\n  → Dauert: 5 Minuten\n  → Ergebnis: Du kennst die wichtigsten Vokabeln (Note: 3)\n\n• 432 Steps = Du liest jede Vokabel 432 mal\n  → Dauert: 15 Minuten\n  → Ergebnis: Du kennst ALLE Vokabeln perfekt! (Note: 1) ← PERFEKT!\n\n• 1000 Steps = Du liest jede Vokabel 1000 mal\n  → Dauert: 40 Minuten\n  → Ergebnis: Du kennst alles perfekt (Note: 1)\n  → ABER: Verschwendete Zeit! 432 war schon genug!\n\nGENAUSO bei der KI:\nMehr Steps = KI 'liest' die Fotos öfter = lernt besser\nABER: Ab einem Punkt bringt mehr nichts, kostet nur Zeit + Geld!\n\n432 Steps ist der SWEET SPOT - perfekte Balance!"
                    example="Verschiedene Szenarien:\n\n1. SCHNELLER TEST (billig, okay Qualität):\n   → 100-200 Steps\n   → Dauert: 5 Minuten\n   → Kosten: 0,50€\n   → Qualität: 6/10\n\n2. STANDARD TRAINING (EMPFOHLEN!):\n   → 432 Steps ← NIMM DAS!\n   → Dauert: 15 Minuten\n   → Kosten: 1,50€\n   → Qualität: 9/10\n\n3. MAXIMUM QUALITY (Verschwendung!):\n   → 1000+ Steps\n   → Dauert: 40+ Minuten\n   → Kosten: 4€\n   → Qualität: 9.5/10 (nur 0.5 besser!)\n\nREGEL: Lass es auf 432! Das ist perfekt!"
                  />
                </label>
                <ExplainText>{explanations.training_steps}</ExplainText>
                
                <div className="flex gap-3 items-center">
                  <input
                    type="range"
                    min="100"
                    max="5000"
                    step="100"
                    value={settings.training_steps}
                    onChange={(e) => updateField('training_steps', parseInt(e.target.value))}
                    disabled={!isEditing}
                    className="flex-1 disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="training-steps-slider"
                  />
                  <input
                    type="number"
                    min="100"
                    max="5000"
                    step="100"
                    value={settings.training_steps}
                    onChange={(e) => updateField('training_steps', parseInt(e.target.value) || 100)}
                    disabled={!isEditing}
                    className="w-24 px-3 py-2 bg-black/60 border border-white/20 rounded-lg text-white text-center focus:border-[#10B981] focus:outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="training-steps-input"
                  />
                </div>
                
                <div className="flex justify-between text-xs text-white/50 mt-1">
                  <span>100</span>
                  <span>5000</span>
                </div>
                {!isEditing && (
                  <div className="mt-2 text-sm text-[#10B981]">
                    Aktuell: <span className="font-semibold">{settings.training_steps} Steps</span>
                  </div>
                )}
              </div>
              
              {/* NEU: Training Width/Height */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">
                    Training Width
                    <HelpIcon 
                      title="Training Breite (Pixel)" 
                      description="Wie breit sollen die Trainingsbilder sein?\n\n1024x1024 = Standard (Quadrat)\n512x512 = Kleiner, schneller\n1536x1536 = Größer, mehr Details\n\nGrößere Bilder = Bessere Details ABER längeres Training!\n\nEMPFEHLUNG: 1024 lassen (Standard)"
                      example="512 → Schnell, wenig Details\n1024 → Standard ← EMPFOHLEN\n1536 → Maximum Details (langsam)"
                    />
                  </label>
                  <input
                    type="number"
                    value={settings.training_width || 1024}
                    onChange={(e) => updateField('training_width', parseInt(e.target.value) || 1024)}
                    disabled={!isEditing}
                    min="512"
                    max="2048"
                    step="64"
                    className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="training-width-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">
                    Training Height
                    <HelpIcon 
                      title="Training Höhe (Pixel)" 
                      description="Wie hoch sollen die Trainingsbilder sein?\n\nMuss normalerweise gleich wie Width sein!\n1024x1024 = Quadrat (Standard)\n\nEMPFEHLUNG: Gleich wie Width!"
                      example="1024x1024 = Quadrat ← STANDARD\n1024x1536 = Hochformat\n1536x1024 = Querformat"
                    />
                  </label>
                  <input
                    type="number"
                    value={settings.training_height || 1024}
                    onChange={(e) => updateField('training_height', parseInt(e.target.value) || 1024)}
                    disabled={!isEditing}
                    min="512"
                    max="2048"
                    step="64"
                    className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="training-height-input"
                  />
                </div>
              </div>
              
              {/* NEU: Training Theme */}
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  Training Theme (Optional)
                  <HelpIcon 
                    title="Training Theme (Style-Variation)" 
                    description="Theme API erlaubt Style-Variationen während des Trainings.\n\nBeispiele: 'vintage', 'modern', 'cinematic', 'artistic'\n\nLeer lassen = Standard (empfohlen)\nNur für spezielle Style-Trainings nutzen!"
                    example="Leer → Standard ← EMPFOHLEN\n'vintage' → Retro-Style Training\n'cinematic' → Film-Look Training"
                  />
                </label>
                <input
                  type="text"
                  value={settings.training_theme || ''}
                  onChange={(e) => updateField('training_theme', e.target.value)}
                  disabled={!isEditing}
                  placeholder="z.B. vintage, cinematic (optional)"
                  className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed placeholder:text-white/30"
                  data-testid="training-theme-input"
                />
              </div>
            </div>
          </div>

          {/* Generation - Core */}
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-6">
              <Zap className="w-6 h-6 text-[#10B981]" />
              <h2 className="text-xl font-bold text-white">Generierung - Core</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  CFG Scale: {settings.cfg_scale}
                  <span className="ml-2 text-white/50">(Guidance Strength)</span>
                  <HelpIcon 
                    title="CFG Scale (Prompt-Befolgung)" 
                    description="Stell dir vor, du bist der Chef und gibst der KI eine Arbeitsanweisung.\n\nCFG Scale = Wie STRENG die KI deiner Anweisung folgen soll:\n\nNIEDRIG (CFG 3):\n• Du: 'Mach mir ein Bild von Julia am Strand'\n• KI: 'Okay Chef! Hmm, vielleicht mit Sonnenuntergang? Oder mit Cocktail? Ich entscheide mal selbst!' (SEHR kreativ, viele Überraschungen)\n\nMITTEL (CFG 7-10) ← PERFEKT!:\n• Du: 'Mach mir ein Bild von Julia am Strand'\n• KI: 'Okay! Julia am Strand, ich mache das genau so. Kleine Details entscheide ich aber selbst.' (Gute Balance!)\n\nHOCH (CFG 15-20):\n• Du: 'Julia am Strand'\n• KI: 'EXAKT Julia. EXAKT Strand. Nichts anderes. Keine Kreativität.' (Sehr genau, aber langweilig)\n\nFür Porträts: 7-10 ist PERFEKT! Genau genug, aber nicht roboterhaft."
                    example="BEISPIEL-PROMPT: 'Julia am Strand bei Sonnenuntergang'\n\nCFG 3 (zu kreativ):\n→ Mal ist Julia jung, mal alt\n→ Mal Cocktail, mal nicht\n→ Mal Bikini, mal Kleid\n→ Zu viel Variation! ✗\n\nCFG 7-10 (PERFEKT!):\n→ Immer Julia (erkennbar)\n→ Immer Strand + Sonnenuntergang\n→ Aber jedes Bild bisschen anders\n→ Perfekte Balance! ✓ ← NIMM DAS!\n\nCFG 18 (zu streng):\n→ Alle Bilder fast identisch\n→ Keine Variation mehr\n→ Langweilig! ✗\n\nREGEL: Lass es auf 7! Das funktioniert immer!"
                  />
                </label>
                <ExplainText>{explanations.cfg_scale}</ExplainText>
                
                <div className="flex gap-3 items-center">
                  <input
                    type="range"
                    min="1"
                    max="20"
                    step="0.1"
                    value={settings.cfg_scale}
                    onChange={(e) => updateField('cfg_scale', parseFloat(e.target.value))}
                    disabled={!isEditing}
                    className="flex-1 disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="cfg-scale-slider"
                  />
                  <input
                    type="number"
                    min="1"
                    max="20"
                    step="0.1"
                    value={settings.cfg_scale}
                    onChange={(e) => updateField('cfg_scale', parseFloat(e.target.value) || 1)}
                    disabled={!isEditing}
                    className="w-20 px-3 py-2 bg-black/60 border border-white/20 rounded-lg text-white text-center focus:border-[#10B981] focus:outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="cfg-scale-input"
                  />
                </div>
                
                <div className="flex justify-between text-xs text-white/50 mt-1">
                  <span>1.0 (Schwach)</span>
                  <span>20.0 (Stark)</span>
                </div>
                {!isEditing && (
                  <div className="mt-2 text-sm text-[#10B981]">
                    Aktuell: <span className="font-semibold">{settings.cfg_scale}</span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  Inference Steps: {settings.steps}
                  <HelpIcon 
                    title="Inference Steps (Rechenschritte)" 
                    description="Stell dir vor, du malst ein Bild. Aber nicht auf einmal, sondern Schritt-für-Schritt:\n\nStep 1: Grobe Skizze (nur Umrisse)\nStep 5: Jetzt erkennst du ein Gesicht\nStep 10: Gesicht hat Augen, Nase, Mund (aber noch grob)\nStep 30: Gesicht sieht gut aus! Details stimmen! ← HIER STOPPEN!\nStep 50: Noch mehr Details (Wimpern, Poren)\nStep 100: MEGA Details (einzelne Haare sichtbar)\n\nMEHR Steps = MEHR Zeit zum Verfeinern = BESSERES Bild\nABER: Ab Step 30 wird der Unterschied kleiner!\n\n10 Steps = 2 Sekunden, grobes Bild (für schnelle Tests)\n30 Steps = 6 Sekunden, gutes Bild (STANDARD!) ← PERFEKT!\n100 Steps = 20 Sekunden, perfektes Bild (lohnt sich selten!)\n\nFür 99% der Fälle: 30 Steps sind mehr als genug!"
                    example="Vergleich mit Julia-Foto am Strand:\n\n10 Steps (2 Sek):\n→ Man erkennt Julia\n→ Aber Gesicht etwas verschwommen\n→ Für schnelle Vorschau OK\n→ Qualität: 6/10 ✗\n\n30 Steps (6 Sek) - STANDARD!:\n→ Julia perfekt erkennbar\n→ Gesicht klar und schön\n→ Beste Balance Zeit/Qualität\n→ Qualität: 9/10 ✓ ← NIMM DAS!\n\n50 Steps (10 Sek):\n→ Minimal besser als 30\n→ Extra Zeit nicht wert!\n→ Qualität: 9.3/10\n\n100 Steps (20 Sek):\n→ Kaum besser als 30!\n→ Reine Zeitverschwendung\n→ Qualität: 9.5/10\n\nREGEL: Lass es auf 30! Schnell UND gut!"
                  />
                </label>
                <ExplainText>{explanations.steps}</ExplainText>
                
                <div className="flex gap-3 items-center">
                  <input
                    type="range"
                    min="10"
                    max="100"
                    step="5"
                    value={settings.steps}
                    onChange={(e) => updateField('steps', parseInt(e.target.value))}
                    disabled={!isEditing}
                    className="flex-1 disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="steps-slider"
                  />
                  <input
                    type="number"
                    min="10"
                    max="100"
                    step="5"
                    value={settings.steps}
                    onChange={(e) => updateField('steps', parseInt(e.target.value) || 10)}
                    disabled={!isEditing}
                    className="w-20 px-3 py-2 bg-black/60 border border-white/20 rounded-lg text-white text-center focus:border-[#10B981] focus:outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="steps-input"
                  />
                </div>
                
                <div className="flex justify-between text-xs text-white/50 mt-1">
                  <span>10 (Schnell)</span>
                  <span>100 (Qualität)</span>
                </div>
                {!isEditing && (
                  <div className="mt-2 text-sm text-[#10B981]">
                    Aktuell: <span className="font-semibold">{settings.steps} Steps</span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  Anzahl Bilder pro Generation: {settings.num_images}
                  <HelpIcon 
                    title="Anzahl Bilder pro Generation" 
                    description="Ganz einfach: Wie viele Bilder soll die KI AUF EINMAL erstellen?\n\n1 Bild:\n• Dauert: 5 Sekunden\n• Problem: Wenn das eine Bild schlecht ist, musst du nochmal generieren!\n• Zeitverschwendung!\n\n4 Bilder (EMPFOHLEN!):\n• Dauert: 20 Sekunden (4x länger)\n• Vorteil: Du hast 4 Optionen zur Auswahl!\n• Mindestens 1-2 gute dabei!\n• Perfekte Balance! ← DAS IST GUT!\n\n8 Bilder:\n• Dauert: 40 Sekunden\n• Vorteil: Viele Optionen\n• Nachteil: Dauert sehr lange, oft zu viel!\n\nWICHTIGER HINWEIS:\nDieser Wert hier ist nur ein FALLBACK (Ersatz-Wert)!\nWenn der User selbst eine Anzahl wählt, wird SEINE Wahl verwendet!\n\nDenk an es wie einen Standard-Wert: 'Falls der User nichts sagt, mach 4 Bilder.'"
                    example="Szenario: User will Julia am Strand generieren\n\nEinstellung hier: 4 Bilder\n\nFall 1 - User wählt NICHTS:\n→ System nutzt unseren Wert: 4 Bilder ✓\n→ User bekommt 4 Optionen\n\nFall 2 - User wählt selbst '8 Bilder':\n→ System ignoriert unseren Wert!\n→ User bekommt 8 Bilder (seine Wahl!) ✓\n\nFall 3 - User wählt '1 Bild':\n→ System ignoriert unseren Wert!\n→ User bekommt 1 Bild (seine Wahl!) ✓\n\nUnser Wert = Nur Standard-Einstellung!\nUser-Wahl = Hat immer Vorrang!\n\nREGEL: Lass es auf 4 - guter Standard!"
                  />
                </label>
                <ExplainText>{explanations.num_images}</ExplainText>
                
                <div className="flex gap-3 items-center">
                  <input
                    type="range"
                    min="1"
                    max="8"
                    step="1"
                    value={settings.num_images}
                    onChange={(e) => updateField('num_images', parseInt(e.target.value))}
                    disabled={!isEditing}
                    className="flex-1 disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="num-images-slider"
                  />
                  <input
                    type="number"
                    min="1"
                    max="8"
                    step="1"
                    value={settings.num_images}
                    onChange={(e) => updateField('num_images', parseInt(e.target.value) || 1)}
                    disabled={!isEditing}
                    className="w-20 px-3 py-2 bg-black/60 border border-white/20 rounded-lg text-white text-center focus:border-[#10B981] focus:outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="num-images-input"
                  />
                </div>
                
                <div className="flex justify-between text-xs text-white/50 mt-1">
                  <span>1</span>
                  <span>8</span>
                </div>
                {!isEditing && (
                  <div className="mt-2 text-sm text-[#10B981]">
                    Aktuell: <span className="font-semibold">{settings.num_images} Bilder</span>
                    <span className="ml-2 text-white/50">(Fallback, User-Auswahl hat Priorität)</span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  Seed (Optional)
                  <span className="ml-2 text-white/50">(für reproduzierbare Ergebnisse)</span>
                  <HelpIcon 
                    title="Seed (Zufallszahl)" 
                    description="Okay, das ist etwas komplex, aber ich erklär's ganz einfach:\n\nStell dir vor, die KI ist wie ein Würfel.\nJedes Mal wenn du ein Bild generierst, würfelt die KI eine Zahl (1-1000000).\nDiese Zahl bestimmt, WIE das Bild genau aussieht.\n\nOHNE SEED (Normal):\n• Prompt: 'Julia am Strand'\n• KI würfelt: 47293 → Julia mit rotem Bikini\n• Nochmal generieren...\n• KI würfelt: 91847 → Julia mit blauem Kleid\n• Jedes Mal ANDERS! (Das ist normal und gut!)\n\nMIT SEED (z.B. Seed = 42):\n• Prompt: 'Julia am Strand'\n• Du sagst: 'Nimm Seed 42!'\n• KI würfelt NICHT, nimmt immer 42 → Julia mit rotem Bikini\n• Nochmal mit Seed 42...\n• Wieder 42 → EXAKT gleiches Bild! Julia mit rotem Bikini!\n\nWANN BRAUCHST DU DAS?\nFast nie! Nur für spezielle Test-Fälle.\n\nREGEL: Lass das Feld LEER! (= Jedes Bild anders)"
                    example="Normal (LEER lassen) - EMPFOHLEN!:\n• Prompt: 'Julia am Strand'\n• Bild 1: Julia, rotes Kleid, lächelt\n• Bild 2: Julia, blaues Top, ernst\n• Bild 3: Julia, weißes Kleid, lacht\n→ Jedes Mal anders! Viel Variation! ✓\n\nMit Seed 42 (nur für Tests!):\n• Prompt: 'Julia am Strand' + Seed 42\n• Bild 1: Julia, rotes Kleid, lächelt\n• Prompt: 'Julia am Strand' + Seed 42\n• Bild 2: Julia, rotes Kleid, lächelt (GLEICH!)\n• Prompt: 'Julia am Strand' + Seed 42\n• Bild 3: Julia, rotes Kleid, lächelt (GLEICH!)\n→ Immer identisch! Nur für Reproduktion!\n\nWANN NUTZEN?\n• Du hast 1 perfektes Bild\n• Willst EXAKT das nochmal (gleiche Pose!)\n→ Dann Seed von dem Bild verwenden\n\nSONST: LEER LASSEN!"
                  />
                </label>
                <ExplainText>{explanations.seed}</ExplainText>
                <input
                  type="number"
                  value={settings.seed || ''}
                  onChange={(e) => updateField('seed', e.target.value ? parseInt(e.target.value) : null)}
                  disabled={!isEditing}
                  placeholder="Leer = Random"
                  className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="seed-input"
                />
                {!isEditing && (
                  <div className="mt-2 text-sm text-[#10B981]">
                    Aktuell: <span className="font-semibold">{settings.seed || 'Random'}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Generation - Advanced */}
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-6">
              <Settings className="w-6 h-6 text-[#10B981]" />
              <h2 className="text-xl font-bold text-white">Generierung - Advanced</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  Standard-Prompt (automatisch)
                  <span className="ml-2 text-white/50">(Wird an jeden Prompt angehängt)</span>
                  <HelpIcon 
                    title="Base Prompt Suffix (Auto-Anhang)" 
                    description="Dieser Text wird AUTOMATISCH an JEDEN Prompt des Users angehängt. Wie eine Unterschrift unter jedem Brief. Nutze es für Qualitäts-Verbesserungen die IMMER gelten sollen. Z.B. 'professional photography, high detail, 8k' sorgt dafür, dass ALLE Bilder professionell aussehen."
                    example="Suffix: 'professional photography, sharp focus, 8k'\n\nUser schreibt: 'woman on beach'\nKI bekommt: 'woman on beach, professional photography, sharp focus, 8k'\n\nSpart Zeit & sorgt für konstante Qualität!"
                  />
                </label>
                <ExplainText>{explanations.base_prompt_suffix}</ExplainText>
                <textarea
                  value={settings.base_prompt_suffix}
                  onChange={(e) => updateField('base_prompt_suffix', e.target.value)}
                  disabled={!isEditing}
                  placeholder="z.B. professional photography, high detail, 8k..."
                  rows="3"
                  className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none transition-all resize-none disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="base-prompt-suffix-input"
                />
                {!isEditing && (
                  <div className="mt-2 text-sm text-[#10B981]">
                    Aktuell: <span className="font-semibold">{settings.base_prompt_suffix || 'Nicht gesetzt'}</span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  Negative Prompt
                  <span className="ml-2 text-white/50">(Was vermieden werden soll)</span>
                  <HelpIcon 
                    title="Negative Prompt (Verbotsliste)" 
                    description="Hier sagst du der KI, was sie NICHT tun soll. Wie eine Verbotsliste. 'blurry, low quality' = Die KI versucht aktiv, das Bild NICHT verschwommen oder schlecht zu machen. Je mehr du hier reinschreibst, desto besser vermeidet die KI diese Fehler!"
                    example="Negative: 'blurry, distorted, bad anatomy, low quality'\n\nKI denkt: 'Okay, ich muss das Bild scharf machen, Anatomie korrekt, hohe Qualität!'\n\nTypische Einträge: blur, distortion, artifacts, noise"
                  />
                </label>
                <ExplainText>{explanations.negative_prompt}</ExplainText>
                <textarea
                  value={settings.negative_prompt}
                  onChange={(e) => updateField('negative_prompt', e.target.value)}
                  disabled={!isEditing}
                  placeholder="z.B. blurry, low quality, distorted..."
                  rows="3"
                  className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none transition-all resize-none disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="negative-prompt-input"
                />
                {!isEditing && (
                  <div className="mt-2 text-sm text-[#10B981]">
                    Aktuell: <span className="font-semibold">{settings.negative_prompt || 'Nicht gesetzt'}</span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  Scheduler
                  <HelpIcon 
                    title="Scheduler (Algorithmus)" 
                    description="Der Scheduler ist der Mathe-Algorithmus, den die KI zum Rechnen benutzt. Wie verschiedene Kochrezepte für das gleiche Gericht. 'DPMSolverMultistep' ist gut für realistische Bilder. 'LCM' ist super schnell. 'Standard' lässt die KI selbst wählen. Für Anfänger: Einfach auf Standard lassen!"
                    example="Standard → KI wählt automatisch ← EMPFOHLEN\nDPMSolverMultistep → Gut für Fotorealismus\nLCM → Sehr schnell, aber weniger Details\n\nNur ändern wenn du weißt was du tust!"
                  />
                </label>
                <select
                  value={settings.scheduler}
                  onChange={(e) => updateField('scheduler', e.target.value)}
                  disabled={!isEditing}
                  className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="scheduler-select"
                >
                  {schedulerOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                {!isEditing && (
                  <div className="mt-2 text-sm text-[#10B981]">
                    Aktuell: <span className="font-semibold">{schedulerOptions.find(o => o.value === settings.scheduler)?.label || 'Standard'}</span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  Similarity Score (Optional)
                  <span className="ml-2 text-white/50">(0.0 - 1.0)</span>
                  <HelpIcon 
                    title="Similarity Score (Ähnlichkeit)" 
                    description="Wie ähnlich soll das generierte Bild der trainierten Person sein? 0.0 = 'Egal, mach was schönes' (kreativ). 1.0 = 'EXAKT wie die Person!' (sehr ähnlich). Für Porträts normalerweise leer lassen oder 0.5-0.8 verwenden."
                    example="Leer → Standard-Ähnlichkeit\n0.3 → Nur grobe Ähnlichkeit, viel Kreativität\n0.8 → Sehr ähnlich zur Trainingsperson\n1.0 → Maximale Ähnlichkeit (fast identisch)"
                  />
                </label>
                <input
                  type="number"
                  min="0"
                  max="1"
                  step="0.1"
                  value={settings.improve_similarity || ''}
                  onChange={(e) => updateField('improve_similarity', e.target.value ? parseFloat(e.target.value) : null)}
                  disabled={!isEditing}
                  placeholder="Leer = Standard"
                  className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="similarity-input"
                />
                {!isEditing && (
                  <div className="mt-2 text-sm text-[#10B981]">
                    Aktuell: <span className="font-semibold">{settings.improve_similarity || 'Standard'}</span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  LoRA Weight: {settings.lora_weight || 1.0}
                  <span className="ml-2 text-white/50">(Stärke des LoRA-Effekts)</span>
                  <HelpIcon 
                    title="LoRA Weight (LoRA-Stärke)" 
                    description="LoRA ist das Training, das du gemacht hast. Dieser Wert bestimmt, wie STARK dieses Training wirkt. 0.0 = Training komplett ignorieren. 1.0 = Normal. 2.0 = Doppelt so stark. Wenn Bilder zu 'generisch' sind, erhöhe den Wert. Wenn sie zu 'übertrieben' sind, senke ihn."
                    example="0.5 → Schwacher Effekt (subtil)\n1.0 → Normal ← EMPFOHLEN\n1.5 → Starker Effekt\n2.0 → Sehr starker Effekt\n\nErhöhen wenn Person nicht erkennbar!"
                  />
                </label>
                
                <div className="flex gap-3 items-center">
                  <input
                    type="range"
                    min="0"
                    max="2"
                    step="0.1"
                    value={settings.lora_weight || 1.0}
                    onChange={(e) => updateField('lora_weight', parseFloat(e.target.value))}
                    disabled={!isEditing}
                    className="flex-1 disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="lora-weight-slider"
                  />
                  <input
                    type="number"
                    min="0"
                    max="2"
                    step="0.1"
                    value={settings.lora_weight || 1.0}
                    onChange={(e) => updateField('lora_weight', parseFloat(e.target.value) || 0)}
                    disabled={!isEditing}
                    className="w-20 px-3 py-2 bg-black/60 border border-white/20 rounded-lg text-white text-center focus:border-[#10B981] focus:outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="lora-weight-input"
                  />
                </div>
                
                <div className="flex justify-between text-xs text-white/50 mt-1">
                  <span>0.0 (Kein Effekt)</span>
                  <span>2.0 (Maximal)</span>
                </div>
                {!isEditing && (
                  <div className="mt-2 text-sm text-[#10B981]">
                    Aktuell: <span className="font-semibold">{settings.lora_weight || 1.0}</span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  Tune Weight: {settings.tune_weight || 1.0}
                  <span className="ml-2 text-white/50">(Stärke des Fine-Tunings)</span>
                  <HelpIcon 
                    title="Tune Weight (Training-Stärke)" 
                    description="Ähnlich wie LoRA Weight, aber für das gesamte Training. 0.0 = Training komplett ignorieren (nutzt nur das Basis-Modell). 1.0 = Normal. 2.0 = Training hat doppelte Wirkung. Niedriger Wert = generischer. Höherer Wert = spezifischer zur trainierten Person."
                    example="0.3 → Mehr Basis-Modell, weniger Training\n1.0 → Ausgewogen ← EMPFOHLEN\n1.5 → Training dominiert\n\nSenken wenn Übertraining sichtbar!"
                  />
                </label>
                
                <div className="flex gap-3 items-center">
                  <input
                    type="range"
                    min="0"
                    max="2"
                    step="0.1"
                    value={settings.tune_weight || 1.0}
                    onChange={(e) => updateField('tune_weight', parseFloat(e.target.value))}
                    disabled={!isEditing}
                    className="flex-1 disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="tune-weight-slider"
                  />
                  <input
                    type="number"
                    min="0"
                    max="2"
                    step="0.1"
                    value={settings.tune_weight || 1.0}
                    onChange={(e) => updateField('tune_weight', parseFloat(e.target.value) || 0)}
                    disabled={!isEditing}
                    className="w-20 px-3 py-2 bg-black/60 border border-white/20 rounded-lg text-white text-center focus:border-[#10B981] focus:outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="tune-weight-input"
                  />
                </div>
                
                <div className="flex justify-between text-xs text-white/50 mt-1">
                  <span>0.0 (Kein Effekt)</span>
                  <span>2.0 (Maximal)</span>
                </div>
                {!isEditing && (
                  <div className="mt-2 text-sm text-[#10B981]">
                    Aktuell: <span className="font-semibold">{settings.tune_weight || 1.0}</span>
                  </div>
                )}
              </div>

              {/* Neue Generation Parameter */}
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  Color Grading
                  <HelpIcon 
                    title="Color Grading (Farb-Look)" 
                    description="Color Grading ist wie ein Instagram-Filter für Farben. 'Film Velvia' = kräftige, satte Farben (wie in der Werbung). 'Film Portra' = weiche, natürliche Hauttöne (perfekt für Porträts). 'Ektar' = warme, goldene Farben (Retro-Look). 'Keine' = Original-Farben."
                    example="Keine → Natürliche Farben\nFilm Velvia → Kräftig, lebendig (Landschaften)\nFilm Portra → Weich, natural (Porträts) ← EMPFOHLEN\nEktar → Warm, golden (Vintage)"
                  />
                </label>
                <select
                  value={settings.color_grading || ''}
                  onChange={(e) => updateField('color_grading', e.target.value)}
                  disabled={!isEditing}
                  className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="color-grading-select"
                >
                  {colorGradingOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                {!isEditing && (
                  <div className="mt-2 text-sm text-[#10B981]">
                    Aktuell: <span className="font-semibold">{colorGradingOptions.find(o => o.value === (settings.color_grading || ''))?.label}</span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  Aspect Ratio
                  <HelpIcon 
                    title="Aspect Ratio (Seitenverhältnis)" 
                    description="Das Format des Bildes. 1:1 = Quadrat (Instagram). 16:9 = Querformat (YouTube). 9:16 = Hochformat (TikTok, Stories). Wenn leer, nutzt die KI ein Standard-Format. Wähle basierend darauf, wo du das Bild verwenden willst!"
                    example="1:1 → Quadrat (Instagram Post)\n16:9 → Querformat (Desktop, YouTube)\n9:16 → Hochformat (Handy, Stories)\n4:5 → Instagram Portrait\n\nFormat an Verwendungszweck anpassen!"
                  />
                </label>
                <select
                  value={settings.aspect_ratio || ''}
                  onChange={(e) => updateField('aspect_ratio', e.target.value)}
                  disabled={!isEditing}
                  className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="aspect-ratio-select"
                >
                  {aspectRatioOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                {!isEditing && (
                  <div className="mt-2 text-sm text-[#10B981]">
                    Aktuell: <span className="font-semibold">{aspectRatioOptions.find(o => o.value === (settings.aspect_ratio || ''))?.label}</span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  ControlNet
                  <HelpIcon 
                    title="ControlNet (Erweiterte Kontrolle)" 
                    description="ControlNet ist ein Profi-Tool für präzise Kontrolle. 'Pose' = KI übernimmt Körperhaltung aus Referenzbild. 'Canny' = KI folgt Kanten/Linien. 'Depth' = 3D-Tiefe vorgeben. FÜR ANFÄNGER: Leer lassen! Nur verwenden wenn du genau weißt was du tust."
                    example="Keine → Standard (für 99% der Fälle) ← EMPFOHLEN\nPose → Übernimmt Körperhaltung\nCanny → Folgt Kanten/Konturen\nDepth → 3D-Tiefenkontrolle\n\nNUR FÜR FORTGESCHRITTENE!"
                  />
                </label>
                <select
                  value={settings.controlnet || ''}
                  onChange={(e) => updateField('controlnet', e.target.value)}
                  disabled={!isEditing}
                  className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="controlnet-select"
                >
                  {controlnetOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                {!isEditing && (
                  <div className="mt-2 text-sm text-[#10B981]">
                    Aktuell: <span className="font-semibold">{controlnetOptions.find(o => o.value === (settings.controlnet || ''))?.label}</span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  Denoising Strength: {settings.denoising_strength || 0.8}
                  <span className="ml-2 text-white/50">(für img2img)</span>
                  <HelpIcon 
                    title="Denoising Strength (Bild-Änderung)" 
                    description="Nur wichtig bei 'img2img' (Bild-zu-Bild). Wenn du ein bestehendes Bild verändern willst: 0.0 = Bild bleibt fast unverändert. 1.0 = Bild wird komplett neu nach Prompt. 0.8 ist ein guter Mittelwert: Behält Struktur, ändert aber Details."
                    example="Ausgangsbild: 'Frau im Büro'\nPrompt: 'Frau am Strand'\n\n0.3 → Bleibt im Büro, nur leichte Änderung\n0.8 → Am Strand, aber Pose ähnlich ← EMPFOHLEN\n1.0 → Komplett neu, Bild fast ignoriert"
                  />
                </label>
                
                <div className="flex gap-3 items-center">
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={settings.denoising_strength || 0.8}
                    onChange={(e) => updateField('denoising_strength', parseFloat(e.target.value))}
                    disabled={!isEditing}
                    className="flex-1 disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="denoising-strength-slider"
                  />
                  <input
                    type="number"
                    min="0"
                    max="1"
                    step="0.1"
                    value={settings.denoising_strength || 0.8}
                    onChange={(e) => updateField('denoising_strength', parseFloat(e.target.value) || 0)}
                    disabled={!isEditing}
                    className="w-20 px-3 py-2 bg-black/60 border border-white/20 rounded-lg text-white text-center focus:border-[#10B981] focus:outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="denoising-strength-input"
                  />
                </div>
                
                <div className="flex justify-between text-xs text-white/50 mt-1">
                  <span>0.0 (Bild behalten)</span>
                  <span>1.0 (Prompt folgen)</span>
                </div>
                {!isEditing && (
                  <div className="mt-2 text-sm text-[#10B981]">
                    Aktuell: <span className="font-semibold">{settings.denoising_strength || 0.8}</span>
                  </div>
                )}
              </div>

              {/* Toggles für Advanced Features */}
              <div className="space-y-3 mt-4">
                {/* NEU: FaceID (Generation) */}
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">
                    FaceID (Generation)
                    <HelpIcon 
                      title="FaceID bei Generation" 
                      description="Nutzt den Gesichts-Fingerabdruck auch bei der Generierung!\n\nWenn FaceID im Training aktiviert war:\n→ Gesichts-Fingerabdruck wurde gespeichert\n→ Bei Generation: Nutzt diesen Fingerabdruck\n→ Ergebnis: Maximale Ähnlichkeit (98%+)!\n\nEMPFEHLUNG: Aktivieren wenn Training auch FaceID hatte!"
                      example="Training mit FaceID + Generation mit FaceID:\n→ Maximale Ähnlichkeit!\n\nTraining mit FaceID + Generation OHNE:\n→ Verschwendung des Fingerabdrucks!"
                    />
                  </label>
                  <div className="text-sm text-white/50 mb-2">💡 Nutzt FaceID für maximale Ähnlichkeit</div>
                  <select
                    value={settings.generation_faceid ? 'true' : 'false'}
                    onChange={(e) => updateField('generation_faceid', e.target.value === 'true')}
                    disabled={!isEditing}
                    className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="generation-faceid-select"
                  >
                    <option value="false">Aus</option>
                    <option value="true">An</option>
                  </select>
                </div>
                
                {/* NEU: Remove Background (Generation) */}
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">
                    Remove Background (Generation)
                    <HelpIcon 
                      title="Remove Background Generation" 
                      description="Schneidet Julia aus dem generierten Bild aus!\n\nOHNE: Julia am Strand (komplettes Bild)\nMIT: Nur Julia (transparenter Hintergrund PNG)\n\nPerfekt für:\n• Profilbilder\n• Produktfotos\n• Später eigenen Hintergrund einfügen"
                      example="Generierung: 'Julia am Strand'\n\nOHNE Remove BG:\n→ Komplettes Bild (JPG)\n→ Julia + Strand zusammen\n\nMIT Remove BG:\n→ Freigestellte Julia (PNG)\n→ Transparenter Hintergrund\n→ Kann in Photoshop verwendet werden!"
                    />
                  </label>
                  <div className="text-sm text-white/50 mb-2">💡 Entfernt Hintergrund (PNG Output)</div>
                  <select
                    value={settings.generation_remove_bg ? 'true' : 'false'}
                    onChange={(e) => updateField('generation_remove_bg', e.target.value === 'true')}
                    disabled={!isEditing}
                    className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="generation-remove-bg-select"
                  >
                    <option value="false">Aus</option>
                    <option value="true">An</option>
                  </select>
                </div>
                
                
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">
                    Use LPW (Weighted Prompts)
                    <HelpIcon 
                      title="Use LPW (Gewichtete Prompts)" 
                      description="Erlaub dir, Teile des Prompts mit (text:1.5) zu gewichten. Wie Lautstärke-Regler für Wörter. 'beautiful (face:1.5)' = KI fokussiert sich EXTRA auf das Gesicht. Normal brauchst du das nicht, aber für Feintuning super nützlich!"
                      example="Ohne LPW: 'beautiful woman with blue eyes'\nMit LPW: 'beautiful woman with (blue:1.5) (eyes:1.5)'\n\nHöhere Zahl = KI fokussiert mehr darauf\n(1.5 = 50% mehr Fokus)"
                    />
                  </label>
                  <div className="text-sm text-white/50 mb-2">💡 Aktiviert gewichtete Prompts</div>
                  <select
                    value={settings.use_lpw ? 'true' : 'false'}
                    onChange={(e) => updateField('use_lpw', e.target.value === 'true')}
                    disabled={!isEditing}
                    className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="use-lpw-select"
                  >
                    <option value="false">Aus</option>
                    <option value="true">An</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">
                    Film Grain
                    <HelpIcon 
                      title="Film Grain (Analog-Look)" 
                      description="Fügt dem Bild feines 'Körnung' hinzu, wie bei alten analogen Filmen. Macht Bilder weniger 'zu perfekt' und gibt ihnen einen vintage, professionellen Film-Look. Wie ein leichter Rausch-Effekt. Gut für künstlerische, retro Styles."
                      example="Deaktiviert → Sauberes, digitales Bild\nAktiviert → Vintage Film-Look mit Körnung\n\nGut für: Retro, Vintage, Film-Aesthetik\nSchlecht für: Moderne, cleane Looks"
                    />
                  </label>
                  <div className="text-sm text-white/50 mb-2">💡 Analog Film Look mit Körnung</div>
                  <select
                    value={settings.film_grain ? 'true' : 'false'}
                    onChange={(e) => updateField('film_grain', e.target.value === 'true')}
                    disabled={!isEditing}
                    className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="film-grain-select"
                  >
                    <option value="false">Aus</option>
                    <option value="true">An</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Styles & Quality Features */}
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-6">
              <Palette className="w-6 h-6 text-[#10B981]" />
              <h2 className="text-xl font-bold text-white">Styles & Quality</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  Style Preset
                  <HelpIcon 
                    title="Style Preset (Visueller Stil)" 
                    description="Stell dir vor, du gibst einem Fotografen eine Anweisung, wie das Foto aussehen soll. Der Style Preset ist wie verschiedene 'Foto-Filter' oder 'Kunst-Stile':\n\n📸 PHOTOGRAPHIC (EMPFOHLEN für Porträts!):\n• Wie ein echtes Foto mit einer professionellen Kamera\n• Realistisch, natürliche Farben, echte Beleuchtung\n• Perfekt für: Porträts, realistische Szenen, Business-Fotos\n• Denk an: iPhone-Foto oder Spiegelreflex-Kamera\n\n🎬 CINEMATIC:\n• Wie ein Film-Still aus einem Hollywood-Film\n• Dramatische Beleuchtung, Film-Farben, episch\n• Perfekt für: Dramatische Szenen, Action, Emotion\n• Denk an: Game of Thrones, Breaking Bad\n\n🎨 DIGITAL ART:\n• Wie ein digitales Gemälde\n• Künstlerisch, gemalt aussehend, nicht fotorealistisch\n• Perfekt für: Kreative Projekte, Poster, Kunst\n• Denk an: DeviantArt, digitale Illustration\n\n📚 COMIC BOOK:\n• Wie ein Superhelden-Comic\n• Starke Konturen, Pop-Art, gezeichnet\n• Perfekt für: Comics, Cartoons, lustige Bilder\n• Denk an: Marvel/DC Comics\n\n🌃 NEON PUNK:\n• Cyberpunk-Style mit Neon-Lichtern\n• Futuristisch, dunkle Farben + helle Neons\n• Perfekt für: Sci-Fi, Zukunft, Club-Szenen\n• Denk an: Blade Runner, Cyberpunk 2077\n\nFür 90% der Fälle: Nimm PHOTOGRAPHIC! Das macht realistische Fotos."
                    example="Beispiel-Prompt: 'Julia in der Stadt'\n\n📸 PHOTOGRAPHIC (Standard):\n→ Sieht aus wie echtes Foto\n→ Julia mit iPhone fotografiert\n→ Realistische Straßen-Szene\n→ Normale Tageslicht-Farben\n→ Perfekt für Portfolio/Social Media ← NIMM DAS!\n\n🎬 CINEMATIC:\n→ Sieht aus wie Film-Szene\n→ Dramatisches Licht (Gegenlicht, Schatten)\n→ Film-Farben (entsättigt, kontrastreich)\n→ Julia wie Film-Hauptdarstellerin\n\n🎨 DIGITAL ART:\n→ Sieht aus wie digitales Gemälde\n→ Künstlerische Pinselstriche sichtbar\n→ Nicht fotorealistisch!\n→ Julia wie gemaltes Porträt\n\n📚 COMIC BOOK:\n→ Sieht aus wie gezeichnet\n→ Starke schwarze Outlines\n→ Comic-Schattierung\n→ Julia wie Superhelden-Comic\n\n🌃 NEON PUNK:\n→ Dunkle Stadt mit Neon-Lichtern\n→ Cyberpunk-Ästhetik\n→ Futuristische Stimmung\n→ Julia in Blade-Runner-Welt\n\nREGEL FÜR PORTRÄTS:\n• Photographic = 95% der Fälle ← STANDARD!\n• Cinematic = Nur für dramatische/künstlerische Bilder\n• Andere = Nur für spezielle kreative Projekte\n\nIM ZWEIFEL: Photographic wählen!"
                  />
                </label>
                <select
                  value={settings.style}
                  onChange={(e) => updateField('style', e.target.value)}
                  disabled={!isEditing}
                  className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="style-select"
                >
                  {styleOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                {!isEditing && (
                  <div className="mt-2 text-sm text-[#10B981]">
                    Aktuell: <span className="font-semibold">{styleOptions.find(o => o.value === settings.style)?.label || 'Standard'}</span>
                  </div>
                )}
              </div>
              
              {/* NEU: Generation Theme, Outpaint, Mask */}
              <div className="space-y-4 mt-6">
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">
                    Generation Theme (Optional)
                    <HelpIcon 
                      title="Generation Theme" 
                      description="Style-Variation bei der Generierung!\n\nBeispiele: 'vintage', 'cinematic', 'modern'\n\nLeer = Standard (empfohlen)"
                      example="Leer → Normal\n'vintage' → Retro-Look\n'cinematic' → Film-Style"
                    />
                  </label>
                  <input
                    type="text"
                    value={settings.generation_theme || ''}
                    onChange={(e) => updateField('generation_theme', e.target.value)}
                    disabled={!isEditing}
                    placeholder="z.B. vintage, cinematic"
                    className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed placeholder:text-white/30"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">
                    Outpaint (Optional)
                    <HelpIcon 
                      title="Outpaint (Bild-Erweiterung)" 
                      description="Erweitert das Bild über die Grenzen hinaus!\n\nBeispiel: Porträt → Ganzköper\nOder: Quadrat → Cinematic Widescreen\n\nSyntax: 'left', 'right', 'top', 'bottom'\n\nLeer = Keine Erweiterung (Standard)"
                      example="'left,right' → Bild wird links+rechts erweitert\n'bottom' → Bild wird nach unten erweitert\n\nNur für spezielle Use-Cases!"
                    />
                  </label>
                  <input
                    type="text"
                    value={settings.outpaint || ''}
                    onChange={(e) => updateField('outpaint', e.target.value)}
                    disabled={!isEditing}
                    placeholder="z.B. left,right oder bottom"
                    className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed placeholder:text-white/30"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">
                    Prompt Mask (Optional)
                    <HelpIcon 
                      title="Prompt Mask (Privatsphäre)" 
                      description="Maskiert/versteckt Teile des Bildes!\n\nBeispiel: 'blur background' = Hintergrund wird unscharf\n\nFür Privatsphäre oder Fokus auf Person.\n\nLeer = Keine Maskierung (Standard)"
                      example="'blur background' → Hintergrund verschwommen\n'hide background' → Hintergrund entfernt\n\nNur für spezielle Privatsphäre-Szenarien!"
                    />
                  </label>
                  <input
                    type="text"
                    value={settings.mask || ''}
                    onChange={(e) => updateField('mask', e.target.value)}
                    disabled={!isEditing}
                    placeholder="z.B. blur background"
                    className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed placeholder:text-white/30"
                  />
                </div>
              </div>

              {/* Toggle Switches */}
              <div className="space-y-4 mt-6">
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">
                    Super Resolution
                    <HelpIcon 
                      title="Super Resolution (4x Vergrößerung)" 
                      description="Stell dir vor, du hast ein kleines Bild (512x512 Pixel) und willst ein Riesen-Poster daraus machen.\n\nOHNE Super Resolution:\n• Bild bleibt klein (512x512)\n• Wenn du es vergrößerst → verpixelt und unscharf\n• Nur für Web/Social Media geeignet\n\nMIT Super Resolution:\n• KI vergrößert Bild auf 2048x2048 (4x größer!)\n• KI 'erfindet' fehlende Details intelligent dazu\n• Bild bleibt scharf und detailliert\n• Perfekt für Druck und große Formate!\n\nWICHTIG: Dauert 3-4x länger! Nur aktivieren wenn du wirklich große, hochauflösende Bilder brauchst.\n\nDenk daran: Mehr Pixel = mehr Details = längere Generierungszeit + höhere Kosten!"
                      example="Szenario: Julia-Porträt für verschiedene Zwecke\n\nOHNE Super Resolution (Standard):\n→ 512x512 Pixel\n→ Generierung: 6 Sekunden\n→ Perfekt für: Instagram, Website, Vorschau\n→ NICHT gut für: Druck, Poster, Magazine\n\nMIT Super Resolution:\n→ 2048x2048 Pixel (4x Fläche!)\n→ Generierung: 20-25 Sekunden\n→ Perfekt für: Poster, Druck, Magazine, Großformat\n→ Dateigröße: 4-5 MB statt 500 KB\n\nWANN AKTIVIEREN?\n✓ Professioneller Druck geplant\n✓ Poster/Großformat benötigt\n✓ Maximale Detail-Qualität wichtig\n\nWANN NICHT?\n✗ Nur für Social Media (Verschwendung!)\n✗ Schnelle Vorschau gewünscht\n✗ Web-Nutzung (512x512 reicht!)\n\nFAUSTREGEL: Leer lassen! Nur für spezielle Fälle aktivieren."
                    />
                  </label>
                  <div className="text-sm text-white/50 mb-2">💡 {explanations.super_resolution}</div>
                  <select
                    value={settings.super_resolution ? 'true' : 'false'}
                    onChange={(e) => updateField('super_resolution', e.target.value === 'true')}
                    disabled={!isEditing}
                    className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="super-resolution-select"
                  >
                    <option value="false">Aus</option>
                    <option value="true">An</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">
                    Face Correct
                    <HelpIcon 
                      title="Face Correct (Gesichts-Korrektur)" 
                      description="Okay, pass auf - das ist ANDERS als 'Training Face Correct'!\n\nTraining Face Correct = Verbessert Fotos VOR dem Training\nFace Correct (hier) = Verbessert generierte Bilder NACH der Generation\n\nWIE FUNKTIONIERT'S?\n1. KI generiert Julia-Bild am Strand\n2. DANN schaut ein spezielles Gesichts-Tool drüber\n3. Es repariert: Unscharfe Augen, komische Nasen, unreine Haut, Asymmetrien\n4. Ergebnis: Perfekteres Gesicht!\n\nWANN GUT?\n• Manchmal macht die KI leicht 'seltsame' Gesichter\n• Augen schauen in verschiedene Richtungen\n• Nase ist leicht schief\n• Haut hat Artefakte\n\nFace Correct = Automatischer 'Gesichts-Arzt' der solche Fehler repariert!\n\nKOSTEN: Dauert 2-3 Sekunden extra pro Bild."
                      example="Beispiel-Szenario: Julia am Strand generiert\n\nOHNE Face Correct:\n→ Bild 1: Augen schauen leicht schielend\n→ Bild 2: Nase etwas asymmetrisch\n→ Bild 3: Hautton ungleichmäßig\n→ Von 4 Bildern sind 2 'okay', 2 haben kleine Fehler\n\nMIT Face Correct:\n→ Alle 4 Bilder: Augen perfekt gerade\n→ Alle Gesichter symmetrisch\n→ Hauttöne gleichmäßig und glatt\n→ Alle 4 Bilder sind nutzbar!\n\nWANN AKTIVIEREN?\n✓ Professionelle Fotos (jedes Detail zählt)\n✓ Gesichter sind wichtigster Teil des Bildes\n✓ Du willst maximale Qualität\n\nWANN NICHT?\n✗ Vollkörper-Shots (Gesicht klein)\n✗ Künstlerische/abstrakte Bilder\n✗ Geschwindigkeit wichtiger als Perfektion\n\nEMPFEHLUNG: Bei Porträts aktivieren!\nBei Ganzkörper-Shots: Optional."
                    />
                  </label>
                  <div className="text-sm text-white/50 mb-2">💡 {explanations.face_correct}</div>
                  <select
                    value={settings.face_correct ? 'true' : 'false'}
                    onChange={(e) => updateField('face_correct', e.target.value === 'true')}
                    disabled={!isEditing}
                    className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="face-correct-select"
                  >
                    <option value="false">Aus</option>
                    <option value="true">An</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">
                    Inpaint Faces
                    <HelpIcon 
                      title="Inpaint Faces (Gesichter nachzeichnen)" 
                      description="Stell dir vor, du hast ein schönes Bild gemalt, aber das Gesicht sieht komisch aus. Was machst du? Du radierst nur das Gesicht weg und malst es NEU, während der Rest des Bildes gleich bleibt!\n\nGENAU das macht Inpaint Faces:\n\n1. KI generiert komplettes Bild (Julia am Strand)\n2. Inpaint schaut: 'Wo ist das Gesicht?'\n3. Inpaint radiert NUR das Gesicht weg\n4. Inpaint malt das Gesicht komplett NEU (mit mehr Details)\n5. Rest des Bildes bleibt EXAKT gleich\n\nERGEBNIS: Gesichter werden glatter, schöner, detaillierter!\n\nUNTERSCHIED zu Face Correct?\n• Face Correct = Repariert/korrigiert bestehendes Gesicht\n• Inpaint Faces = Malt Gesicht komplett NEU\n\nInpaint ist RADIKALER aber auch BESSER für perfekte Gesichter!"
                      example="Szenario: Julia-Porträt generiert\n\nOHNE Inpaint Faces:\n→ Gesicht: Okay, aber kleine Unschärfen\n→ Hauttextur: Leicht körnig\n→ Details: 7/10\n→ Nutzbar aber nicht perfekt\n\nMIT Inpaint Faces:\n→ Gesicht: Kristallklar und glatt\n→ Hauttextur: Makellos\n→ Details: 10/10\n→ Magazine-Qualität!\n\nKOMBINATION MÖGLICH:\nFace Correct + Inpaint Faces = DOPPELT GUT!\n→ Face Correct repariert grobe Fehler\n→ Inpaint macht Gesicht dann perfekt\n\nWANN AKTIVIEREN?\n✓ Professionelle Porträts\n✓ Gesicht ist Haupt-Fokus\n✓ Maximale Qualität gefordert\n✓ Magazine/Werbung/Print\n\nWANN NICHT?\n✗ Gesicht nur klein im Bild\n✗ Ganzkörper-Shots\n✗ Geschwindigkeit wichtiger\n\nKOSTEN: 3-4 Sekunden extra pro Bild\n\nEMPFEHLUNG FÜR PORTRÄTS:\nFace Correct AN + Inpaint Faces AN = Perfekt!"
                    />
                  </label>
                  <div className="text-sm text-white/50 mb-2">💡 {explanations.inpaint_faces}</div>
                  <select
                    value={settings.inpaint_faces ? 'true' : 'false'}
                    onChange={(e) => updateField('inpaint_faces', e.target.value === 'true')}
                    disabled={!isEditing}
                    className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="inpaint-faces-select"
                  >
                    <option value="false">Aus</option>
                    <option value="true">An</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">
                    Face Swap
                    <HelpIcon 
                      title="Face Swap (Gesichts-Austausch)" 
                      description="Das ist EXTREM! Pass auf:\n\nNormal generiert die KI ein Gesicht BASIEREND auf den Trainingsbildern. Es SIEHT AUS wie Julia, aber ist nicht 100% EXAKT das gleiche Gesicht.\n\nMIT Face Swap:\n1. KI generiert Bild (Julia am Strand, schöne Pose)\n2. Face Swap nimmt ein ECHTES Gesicht aus den Trainingsbildern\n3. Face Swap KLEBT dieses echte Gesicht auf das generierte Bild\n4. Ergebnis: EXAKT Julias Gesicht aus den Trainingsbildern!\n\nDENK AN PHOTOSHOP:\n• Du hast ein Foto von Julia (frontal)\n• Du hast ein generiertes Bild (Julia von der Seite am Strand)\n• Face Swap = Julias ECHTES Gesicht wird auf das generierte Bild geklebt\n• Ergebnis: 100% Julias echtes Gesicht, aber in neuer Pose/Szene!\n\nWARUM GUT?\n• Maximale Ähnlichkeit zur echten Person (100%)\n• Keine KI-Artefakte im Gesicht\n• Echtes Foto-Gesicht in KI-Szene\n\nWARUM VORSICHT?\n• Manchmal passt das Gesicht nicht perfekt zur Pose\n• Beleuchtung kann anders sein (Gesicht vs Körper)\n• Kann 'aufgeklebt' aussehen wenn nicht gut gemacht"
                      example="Vergleich der Techniken:\n\nNORMALE GENERIERUNG (Standard):\n→ KI erstellt Gesicht 'im Stil von Julia'\n→ Sieht aus wie Julia: 85%\n→ Natürlich wirkend: 100%\n→ Passt perfekt zur Szene: 100%\n\nMIT Face Swap:\n→ Echtes Julia-Gesicht eingefügt\n→ Sieht aus wie Julia: 100%\n→ Natürlich wirkend: 80% (manchmal leicht 'aufgeklebt')\n→ Passt zur Szene: 85%\n\nKONKRETES BEISPIEL:\nGeneration: 'Julia beim Yoga'\n\nOhne Face Swap:\n→ KI erfindet Julia's Gesicht neu (sieht ähnlich aus)\n→ Passt perfekt zur Yoga-Pose\n→ Beleuchtung stimmt\n→ Aber: Nicht 100% exakt Julia\n\nMit Face Swap:\n→ Echtes Julia-Gesicht aus Trainingsfoto\n→ 100% Julia erkennbar\n→ ABER: Gesicht schaut vielleicht nicht perfekt in Yoga-Richtung\n→ Beleuchtung könnte nicht perfekt passen\n\nWANN NUTZEN?\n✓ Maximale Ähnlichkeit ist KRITISCH\n✓ Offizielle Dokumente/IDs\n✓ Person muss 100% erkennbar sein\n✓ Einfache Posen (frontal, Porträt)\n\nWANN NICHT?\n✗ Extreme Posen (Yoga, Sport, Tanz)\n✗ Dramatische Beleuchtung\n✗ Künstlerische Freiheit wichtiger\n\nFAUSTREGEL: Nur bei speziellen Anforderungen nutzen!"
                    />
                  </label>
                  <div className="text-sm text-white/50 mb-2">💡 {explanations.face_swap}</div>
                  <select
                    value={settings.face_swap ? 'true' : 'false'}
                    onChange={(e) => updateField('face_swap', e.target.value === 'true')}
                    disabled={!isEditing}
                    className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="face-swap-select"
                  >
                    <option value="false">Aus</option>
                    <option value="true">An</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">
                    Hires Fix
                    <HelpIcon 
                      title="Hires Fix (Hochauflösungs-Reparatur)" 
                      description="Okay, das ist technisch aber ich erklär's einfach:\n\nPROBLEM:\nWenn du große Bilder generierst (z.B. 1024x1024), macht die KI manchmal Fehler:\n• Details sind verschwommen\n• Gesichter sind 'matschig'\n• Kleine Objekte sehen komisch aus\n• Qualität ist nicht gleichmäßig\n\nWARUM? Große Bilder sind schwerer für die KI zu generieren!\n\nHires Fix LÖSUNG:\n1. KI generiert ERST ein kleines Bild (512x512) - das klappt perfekt!\n2. DANN vergrößert Hires Fix auf groß (1024x1024)\n3. Während Vergrößerung: Fügt Details intelligent hinzu\n4. Ergebnis: Großes Bild mit perfekten Details!\n\nDENK AN STAFFELEI-MALER:\nOhne Hires Fix = Maler malt direkt auf riesige Leinwand (schwierig!)\nMit Hires Fix = Maler macht erst kleine Skizze (einfach!), dann vergrößert auf große Leinwand mit allen Details!\n\nERGEBNIS: Bessere Details in großen Bildern!\n\nABER: Dauert 2-3x länger!"
                      example="Praktischer Vergleich:\n\nKLEINE BILDER (512x512):\n→ Hires Fix = NICHT NÖTIG\n→ Funktioniert schon perfekt ohne!\n\nGROSSE BILDER (1024x1024 oder größer):\n\nOhne Hires Fix:\n→ Gesichts-Details: 7/10 (etwas verschwommen)\n→ Kleine Objekte: 6/10 (unscharf)\n→ Generierung: 8 Sekunden\n→ Problem: Qualität leidet bei Größe\n\nMit Hires Fix:\n→ Gesichts-Details: 9/10 (scharf & klar!)\n→ Kleine Objekte: 9/10 (detailliert)\n→ Generierung: 20 Sekunden (2.5x länger!)\n→ Vorteil: Perfekte Qualität auch bei Größe!\n\nKONKRETES BEISPIEL:\nJulia-Porträt 1024x1024\n\nOhne Hires Fix:\n• Augen: Leicht unscharf\n• Haare: Details fehlen\n• Hintergrund: Verschwommen\n\nMit Hires Fix:\n• Augen: Kristallklar, Wimpern sichtbar\n• Haare: Einzelne Strähnen erkennbar\n• Hintergrund: Scharf und detailliert\n\nWANN AKTIVIEREN?\n✓ Große Bilder (1024x1024+)\n✓ Details sind SEHR wichtig\n✓ Print/Druck geplant\n✓ Professionelle Qualität nötig\n\nWANN NICHT?\n✗ Kleine Bilder (512x512 oder kleiner)\n✗ Schnelle Vorschau gewünscht\n✗ Web/Social Media (reicht ohne)\n\nKOMBINATION:\nHires Fix + Super Resolution = MAXIMUM Details!\n(Aber auch MAXIMUM Zeit: 40+ Sekunden pro Bild!)\n\nREGEL:\n• Kleine Bilder → AUS\n• Große Bilder + Zeit egal → AN\n• Große Bilder + schnell → AUS, akzeptiere leichte Qualitäts-Verluste"
                    />
                  </label>
                  <div className="text-sm text-white/50 mb-2">💡 {explanations.hires_fix}</div>
                  <select
                    value={settings.hires_fix ? 'true' : 'false'}
                    onChange={(e) => updateField('hires_fix', e.target.value === 'true')}
                    disabled={!isEditing}
                    className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="hires-fix-select"
                  >
                    <option value="false">Aus</option>
                    <option value="true">An</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* Dropdown Management Section */}
        {dropdowns && (
          <div className="mt-12">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <List className="w-7 h-7 text-[#10B981]" />
                <div>
                  <h2 className="text-2xl font-bold text-white">User-Dropdown Optionen</h2>
                  <p className="text-white/60 text-sm mt-1">Verwalten Sie alle Dropdown-Optionen für User</p>
                </div>
              </div>
              
              {!isEditingDropdowns ? (
                <button
                  onClick={() => setIsEditingDropdowns(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-[#10B981] hover:bg-[#0EA574] text-black font-semibold rounded-xl transition-all"
                >
                  <Edit2 className="w-4 h-4" />
                  Dropdowns Bearbeiten
                </button>
              ) : (
                <div className="flex gap-3">
                  <button
                    onClick={saveDropdowns}
                    disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 bg-[#10B981] hover:bg-[#0EA574] text-black font-semibold rounded-xl transition-all"
                  >
                    <Save className="w-4 h-4" />
                    Speichern
                  </button>
                  <button
                    onClick={() => setIsEditingDropdowns(false)}
                    className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-xl transition-all"
                  >
                    <X className="w-4 h-4" />
                    Abbrechen
                  </button>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 gap-6">
              {/* Tageszeit */}
              <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <span>☀️</span> Tageszeit
                </h3>
                <div className="text-xs text-white/50 mb-3">Anzahl Optionen: {Object.keys(dropdowns.time_of_day || {}).length}</div>
                
                {isEditingDropdowns ? (
                  <div className="space-y-3">
                    {Object.entries(dropdowns.time_of_day || {}).map(([key, data]) => (
                      <div key={key} className="p-3 bg-black/40 rounded-lg space-y-2">
                        <div className="flex gap-2 items-center">
                          <input
                            value={key}
                            disabled
                            className="w-32 px-3 py-2 bg-black/80 border border-white/10 rounded-lg text-white/50 text-sm cursor-not-allowed"
                          />
                          <input
                            value={data.label_de || ''}
                            onChange={(e) => updateDropdownField('time_of_day', key, 'label_de', e.target.value)}
                            placeholder="Label (Deutsch) - z.B. 'Tag'"
                            className="flex-1 px-3 py-2 bg-black/60 border border-white/20 rounded-lg text-white text-sm focus:border-[#10B981] focus:outline-none"
                          />
                          <button
                            onClick={() => deleteDropdownOption('time_of_day', key)}
                            className="p-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg transition-all"
                          >
                            <X className="w-4 h-4 text-red-400" />
                          </button>
                        </div>
                        <input
                          value={data.desc_de || ''}
                          onChange={(e) => updateDropdownField('time_of_day', key, 'desc_de', e.target.value)}
                          placeholder="Beschreibung (Deutsch) - z.B. 'helles Tageslicht'"
                          className="w-full px-3 py-2 bg-black/60 border border-white/20 rounded-lg text-white text-sm focus:border-[#10B981] focus:outline-none"
                        />
                        <textarea
                          value={data.keywords || ''}
                          onChange={(e) => updateDropdownField('time_of_day', key, 'keywords', e.target.value)}
                          placeholder="Keywords (Englisch, an Astria)"
                          rows="4"
                          className="w-full px-3 py-2 bg-black/60 border border-white/20 rounded-lg text-white text-sm focus:border-[#10B981] focus:outline-none resize-y"
                        />
                      </div>
                    ))}
                    <button
                      onClick={() => {
                        const newKey = prompt('Neuer Key (Englisch, z.B. "dusk"):');
                        if (newKey) addDropdownOption('time_of_day', newKey);
                      }}
                      className="w-full p-2 bg-[#10B981]/20 hover:bg-[#10B981]/30 border border-[#10B981]/30 rounded-lg text-[#10B981] text-sm font-medium transition-all flex items-center justify-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Option hinzufügen
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2 text-sm text-white/70">
                    {Object.keys(dropdowns.time_of_day || {}).map(key => (
                      <div key={key} className="p-2 bg-black/40 rounded">
                        <span className="text-[#10B981] font-semibold">{key}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Wetter */}
              <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <span>🌦️</span> Wetter
                </h3>
                <div className="text-xs text-white/50 mb-3">Anzahl Optionen: {Object.keys(dropdowns.weather || {}).length}</div>
                
                {isEditingDropdowns ? (
                  <div className="space-y-3">
                    {Object.entries(dropdowns.weather || {}).map(([key, data]) => {
                      const isOldFormat = typeof data === 'string';
                      return (
                        <div key={key} className="p-3 bg-black/40 rounded-lg space-y-2">
                          <div className="flex gap-2">
                            <input
                              value={key}
                              disabled
                              className="w-32 px-3 py-2 bg-black/80 border border-white/10 rounded-lg text-white/50 text-sm cursor-not-allowed"
                            />
                            <input
                              value={isOldFormat ? '' : (data.label_de || '')}
                              onChange={(e) => updateDropdownField('weather', key, 'label_de', e.target.value)}
                              placeholder="Label (Deutsch, für User)"
                              className="flex-1 px-3 py-2 bg-black/60 border border-white/20 rounded-lg text-white text-sm focus:border-[#10B981] focus:outline-none"
                            />
                            <button
                              onClick={() => deleteDropdownOption('weather', key)}
                              className="p-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg transition-all"
                            >
                              <X className="w-4 h-4 text-red-400" />
                            </button>
                          </div>
                          <input
                            value={isOldFormat ? '' : (data.desc_de || '')}
                            onChange={(e) => updateDropdownField('weather', key, 'desc_de', e.target.value)}
                            placeholder="Beschreibung (Deutsch) - z.B. 'strahlend, wolkenlos'"
                            className="w-full px-3 py-2 bg-black/60 border border-white/20 rounded-lg text-white text-sm focus:border-[#10B981] focus:outline-none"
                          />
                          <textarea
                            value={isOldFormat ? data : (data.keywords || '')}
                            onChange={(e) => updateDropdownField('weather', key, 'keywords', e.target.value)}
                            placeholder="Keywords (Englisch, an Astria)"
                            rows="4"
                            className="w-full px-3 py-2 bg-black/60 border border-white/20 rounded-lg text-white text-sm focus:border-[#10B981] focus:outline-none resize-y"
                          />
                        </div>
                      );
                    })}
                    <button
                      onClick={() => {
                        const newKey = prompt('Neuer Key:');
                        if (newKey) addDropdownOption('weather', newKey);
                      }}
                      className="w-full p-2 bg-[#10B981]/20 hover:bg-[#10B981]/30 border border-[#10B981]/30 rounded-lg text-[#10B981] text-sm font-medium transition-all flex items-center justify-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Option hinzufügen
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2 text-sm text-white/70">
                    {Object.keys(dropdowns.weather || {}).map(key => (
                      <div key={key} className="p-2 bg-black/40 rounded">
                        <span className="text-[#10B981] font-semibold">{key}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Kamera */}
              <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <span>📷</span> Kamera
                </h3>
                <div className="text-xs text-white/50 mb-3">Anzahl Optionen: {Object.keys(dropdowns.camera || {}).length}</div>
                
                {isEditingDropdowns ? (
                  <div className="space-y-3">
                    {Object.entries(dropdowns.camera || {}).map(([key, data]) => {
                      const isOldFormat = typeof data === 'string';
                      return (
                        <div key={key} className="p-3 bg-black/40 rounded-lg space-y-2">
                          <div className="flex gap-2">
                            <input
                              value={key}
                              disabled
                              className="w-32 px-3 py-2 bg-black/80 border border-white/10 rounded-lg text-white/50 text-sm cursor-not-allowed"
                            />
                            <input
                              value={isOldFormat ? '' : (data.label_de || '')}
                              onChange={(e) => updateDropdownField('camera', key, 'label_de', e.target.value)}
                              placeholder="Label (Deutsch)"
                              className="flex-1 px-3 py-2 bg-black/60 border border-white/20 rounded-lg text-white text-sm focus:border-[#10B981] focus:outline-none"
                            />
                            <button
                              onClick={() => deleteDropdownOption('camera', key)}
                              className="p-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg transition-all"
                            >
                              <X className="w-4 h-4 text-red-400" />
                            </button>
                          </div>
                          <input
                            value={isOldFormat ? '' : (data.desc_de || '')}
                            onChange={(e) => updateDropdownField('camera', key, 'desc_de', e.target.value)}
                            placeholder="Beschreibung (Deutsch) - z.B. 'Große Szenen'"
                            className="w-full px-3 py-2 bg-black/60 border border-white/20 rounded-lg text-white text-sm focus:border-[#10B981] focus:outline-none"
                          />
                          <textarea
                            value={isOldFormat ? data : (data.keywords || '')}
                            onChange={(e) => updateDropdownField('camera', key, 'keywords', e.target.value)}
                            placeholder="Keywords (Englisch)"
                            rows="4"
                            className="w-full px-3 py-2 bg-black/60 border border-white/20 rounded-lg text-white text-sm focus:border-[#10B981] focus:outline-none resize-y"
                          />
                        </div>
                      );
                    })}
                    <button
                      onClick={() => {
                        const newKey = prompt('Neuer Key:');
                        if (newKey) addDropdownOption('camera', newKey);
                      }}
                      className="w-full p-2 bg-[#10B981]/20 hover:bg-[#10B981]/30 border border-[#10B981]/30 rounded-lg text-[#10B981] text-sm font-medium transition-all flex items-center justify-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Option hinzufügen
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2 text-sm text-white/70">
                    {Object.keys(dropdowns.camera || {}).map(key => (
                      <div key={key} className="p-2 bg-black/40 rounded">
                        <span className="text-[#10B981] font-semibold">{key}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Bildausschnitt (Body Framing) - NEU! */}
              <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <span>🧍</span> Bildausschnitt
                </h3>
                <div className="text-xs text-white/50 mb-3">Anzahl Optionen: {Object.keys(dropdowns.body_framing || {}).length}</div>
                
                {isEditingDropdowns ? (
                  <div className="space-y-3">
                    {Object.entries(dropdowns.body_framing || {}).map(([key, data]) => {
                      const isOldFormat = typeof data === 'string';
                      return (
                        <div key={key} className="p-3 bg-black/40 rounded-lg space-y-2">
                          <div className="flex gap-2">
                            <input
                              value={key}
                              disabled
                              className="w-32 px-3 py-2 bg-black/80 border border-white/10 rounded-lg text-white/50 text-sm cursor-not-allowed"
                            />
                            <input
                              value={isOldFormat ? '' : (data.label || data.label_de || '')}
                              onChange={(e) => updateDropdownField('body_framing', key, 'label', e.target.value)}
                              placeholder="Label (z.B. Ganzkörper)"
                              className="flex-1 px-3 py-2 bg-black/60 border border-white/20 rounded-lg text-white text-sm focus:border-[#10B981] focus:outline-none"
                            />
                            <button
                              onClick={() => deleteDropdownOption('body_framing', key)}
                              className="p-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg transition-all"
                            >
                              <X className="w-4 h-4 text-red-400" />
                            </button>
                          </div>
                          <input
                            value={isOldFormat ? '' : (data.description || data.desc_de || '')}
                            onChange={(e) => updateDropdownField('body_framing', key, 'description', e.target.value)}
                            placeholder="Beschreibung - z.B. 'Kopf bis Fuß sichtbar'"
                            className="w-full px-3 py-2 bg-black/60 border border-white/20 rounded-lg text-white text-sm focus:border-[#10B981] focus:outline-none"
                          />
                          <textarea
                            value={isOldFormat ? data : (data.keywords || '')}
                            onChange={(e) => updateDropdownField('body_framing', key, 'keywords', e.target.value)}
                            placeholder="Keywords (Englisch) - z.B. 'full body shot, entire body visible'"
                            rows="3"
                            className="w-full px-3 py-2 bg-black/60 border border-white/20 rounded-lg text-white text-sm focus:border-[#10B981] focus:outline-none resize-y"
                          />
                        </div>
                      );
                    })}
                    <button
                      onClick={() => {
                        const newKey = prompt('Neuer Key (z.B. close_up):');
                        if (newKey) addDropdownOption('body_framing', newKey);
                      }}
                      className="w-full p-2 bg-[#10B981]/20 hover:bg-[#10B981]/30 border border-[#10B981]/30 rounded-lg text-[#10B981] text-sm font-medium transition-all flex items-center justify-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Option hinzufügen
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2 text-sm text-white/70">
                    {Object.keys(dropdowns.body_framing || {}).map(key => (
                      <div key={key} className="p-2 bg-black/40 rounded">
                        <span className="text-[#10B981] font-semibold">{key}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Bildstile */}
              <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <span>🎨</span> Bildstile
                </h3>
                <div className="text-xs text-white/50 mb-3">Anzahl Optionen: {Object.keys(dropdowns.styles || {}).length}</div>
                
                {isEditingDropdowns ? (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {Object.entries(dropdowns.styles || {}).map(([key, data]) => {
                      const isOldFormat = typeof data === 'string';
                      return (
                        <div key={key} className="p-3 bg-black/40 rounded-lg space-y-2">
                          <div className="flex gap-2">
                            <input
                              value={key}
                              disabled
                              className="w-40 px-3 py-2 bg-black/80 border border-white/10 rounded-lg text-white/50 text-sm cursor-not-allowed"
                            />
                            <input
                              value={isOldFormat ? '' : (data.label_de || '')}
                              onChange={(e) => updateDropdownField('styles', key, 'label_de', e.target.value)}
                              placeholder="Label (Deutsch) - z.B. 'Filmisch - Episch'"
                              className="flex-1 px-3 py-2 bg-black/60 border border-white/20 rounded-lg text-white text-sm focus:border-[#10B981] focus:outline-none"
                            />
                            <button
                              onClick={() => deleteDropdownOption('styles', key)}
                              className="p-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg transition-all"
                            >
                              <X className="w-4 h-4 text-red-400" />
                            </button>
                          </div>
                          <input
                            value={isOldFormat ? '' : (data.desc_de || '')}
                            onChange={(e) => updateDropdownField('styles', key, 'desc_de', e.target.value)}
                            placeholder="Beschreibung (Deutsch) - z.B. 'Episch'"
                            className="w-full px-3 py-2 bg-black/60 border border-white/20 rounded-lg text-white text-sm focus:border-[#10B981] focus:outline-none"
                          />
                          <textarea
                            value={isOldFormat ? data : (data.keywords || '')}
                            onChange={(e) => updateDropdownField('styles', key, 'keywords', e.target.value)}
                            placeholder="Keywords (Englisch, an Astria)"
                            rows="8"
                            className="w-full px-3 py-2 bg-black/60 border border-white/20 rounded-lg text-white text-sm focus:border-[#10B981] focus:outline-none resize-y"
                          />
                        </div>
                      );
                    })}
                    <button
                      onClick={() => {
                        const newKey = prompt('Neuer Stil-Key (Englisch):');
                        if (newKey) addDropdownOption('styles', newKey);
                      }}
                      className="w-full p-2 bg-[#10B981]/20 hover:bg-[#10B981]/30 border border-[#10B981]/30 rounded-lg text-[#10B981] text-sm font-medium transition-all flex items-center justify-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Stil hinzufügen
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2 text-sm text-white/70 max-h-60 overflow-y-auto">
                    {Object.keys(dropdowns.styles || {}).map(key => (
                      <div key={key} className="p-2 bg-black/40 rounded">
                        <span className="text-[#10B981] font-semibold">{key}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Bildformate */}
              <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <span>🖼️</span> Bildformate
                </h3>
                <div className="text-xs text-white/50 mb-3">Anzahl Optionen: {Object.keys(dropdowns.formats || {}).length}</div>
                
                {isEditingDropdowns ? (
                  <div className="space-y-3">
                    {Object.entries(dropdowns.formats || {}).map(([key, data]) => {
                      const isOldFormat = typeof data === 'string';
                      return (
                        <div key={key} className="p-3 bg-black/40 rounded-lg space-y-2">
                          <div className="flex gap-2">
                            <input
                              value={key}
                              disabled
                              className="w-40 px-3 py-2 bg-black/80 border border-white/10 rounded-lg text-white/50 text-sm cursor-not-allowed"
                            />
                            <input
                              value={isOldFormat ? '' : (data.label_de || '')}
                              onChange={(e) => updateDropdownField('formats', key, 'label_de', e.target.value)}
                              placeholder="Label (Deutsch)"
                              className="flex-1 px-3 py-2 bg-black/60 border border-white/20 rounded-lg text-white text-sm focus:border-[#10B981] focus:outline-none"
                            />
                            <button
                              onClick={() => deleteDropdownOption('formats', key)}
                              className="p-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg transition-all"
                            >
                              <X className="w-4 h-4 text-red-400" />
                            </button>
                          </div>
                          <input
                            value={isOldFormat ? '' : (data.desc_de || '')}
                            onChange={(e) => updateDropdownField('formats', key, 'desc_de', e.target.value)}
                            placeholder="Beschreibung (Deutsch)"
                            className="w-full px-3 py-2 bg-black/60 border border-white/20 rounded-lg text-white text-sm focus:border-[#10B981] focus:outline-none"
                          />
                          <div className="flex gap-2">
                            <input
                              type="number"
                              value={isOldFormat ? '' : (data.width || '')}
                              onChange={(e) => updateDropdownField('formats', key, 'width', parseInt(e.target.value))}
                              placeholder="Breite (px)"
                              className="flex-1 px-3 py-2 bg-black/60 border border-white/20 rounded-lg text-white text-sm focus:border-[#10B981] focus:outline-none"
                            />
                            <input
                              type="number"
                              value={isOldFormat ? '' : (data.height || '')}
                              onChange={(e) => updateDropdownField('formats', key, 'height', parseInt(e.target.value))}
                              placeholder="Höhe (px)"
                              className="flex-1 px-3 py-2 bg-black/60 border border-white/20 rounded-lg text-white text-sm focus:border-[#10B981] focus:outline-none"
                            />
                          </div>
                        </div>
                      );
                    })}
                    <button
                      onClick={() => {
                        const newKey = prompt('Neuer Format-Key (z.B. "portrait_4_5"):');
                        if (newKey) addDropdownOption('formats', newKey);
                      }}
                      className="w-full p-2 bg-[#10B981]/20 hover:bg-[#10B981]/30 border border-[#10B981]/30 rounded-lg text-[#10B981] text-sm font-medium transition-all flex items-center justify-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Format hinzufügen
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2 text-sm text-white/70">
                    {Object.keys(dropdowns.formats || {}).map(key => (
                      <div key={key} className="p-2 bg-black/40 rounded">
                        <span className="text-[#10B981] font-semibold">{key}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Info Box */}
        <div className="mt-8 p-6 bg-blue-500/10 border border-blue-500/30 rounded-2xl">
          <div className="flex items-start gap-4">
            <Info className="w-6 h-6 text-blue-400 flex-shrink-0 mt-1" />
            <div className="text-white/80 text-sm space-y-2">
              <p className="font-semibold text-white">Wichtige Hinweise:</p>
              <ul className="list-disc list-inside space-y-1 text-white/70">
                <li>Änderungen wirken sich auf ALLE zukünftigen Bild-Generierungen aus</li>
                <li>Base Model ID muss mit dem Astria-Modell übereinstimmen</li>
                <li>Hohe CFG Scale = stärkere Prompt-Befolgung, aber weniger Kreativität</li>
                <li>Mehr Steps = bessere Qualität, aber längere Generierungszeit</li>
                <li>Nicht alle Modelle unterstützen alle Parameter (z.B. Flux 1 dev hat Einschränkungen bei negative_prompt)</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Last Updated Info */}
        {settings.updated_at && (
          <div className="mt-6 text-center text-white/50 text-sm">
            Zuletzt aktualisiert: {new Date(settings.updated_at).toLocaleString('de-DE')}
            {settings.updated_by && ` von ${settings.updated_by}`}
          </div>
        )}

      </div>

      {/* Help Modal */}
      <Dialog open={helpModalOpen} onOpenChange={setHelpModalOpen}>
        <DialogContent className="bg-[#0A0E1A] border border-white/20 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#10B981] flex items-center gap-2">
              <HelpCircle className="w-6 h-6" />
              {helpContent.title}
            </DialogTitle>
            <DialogDescription className="text-white/80 text-base mt-4 leading-relaxed">
              {helpContent.description}
            </DialogDescription>
          </DialogHeader>
          
          {helpContent.example && (
            <div className="mt-4 p-4 bg-white/5 rounded-xl border border-white/10">
              <div className="text-sm font-semibold text-[#10B981] mb-2">📝 Beispiele:</div>
              <pre className="text-sm text-white/70 whitespace-pre-wrap font-mono leading-relaxed">
                {helpContent.example}
              </pre>
            </div>
          )}
          
          <div className="mt-6 flex justify-end">
            <button
              onClick={() => setHelpModalOpen(false)}
              className="px-6 py-2 bg-[#10B981] hover:bg-[#0EA574] text-black font-semibold rounded-xl transition-all"
              data-testid="help-modal-close-btn"
            >
              Verstanden!
            </button>
          </div>
        </DialogContent>
      </Dialog>

      </div>
    </div>
  );
}
